export interface HomepageStrings {
  // Hero
  hero_h1_main: string;
  hero_h1_highlight: string;
  hero_subtitle: string;
  hero_cta_primary: string;
  hero_cta_secondary: string;
  hero_social_1: string;
  hero_social_2: string;
  hero_social_3: string;
  // Navigation
  nav_features: string;
  nav_themes: string;
  nav_how_it_works: string;
  nav_pricing: string;
  nav_faq: string;
  nav_login: string;
  nav_cta: string;
  nav_app: string;
  // FAQ heading
  faq_heading: string;
  faq_heading_highlight: string;
  faq_subtitle: string;
  faq_contact_link: string;
  faq_contact_suffix: string;
  // Emotive statements
  emotive_statements: Array<{ heading: string; highlight: string; copy: string }>;
  // FAQ items
  faq_items: Array<{ question: string; answer: string }>;
  // HowItWorks
  how_it_works_badge: string;
  how_it_works_h2_pre: string;
  how_it_works_h2_highlight: string;
  how_it_works_h2_post: string;
  how_it_works_body: string;
  how_it_works_steps: Array<{ title: string; description: string }>;
  // HeadlineFeatures
  headline_features_badge: string;
  headline_features_h2_pre: string;
  headline_features_h2_highlight: string;
  headline_features_body: string;
  headline_feature_blocks: Array<{ heading_pre: string; heading_highlight: string; heading_post: string; copy: string; cta: string }>;
  // FeaturesGrid
  features_grid_badge: string;
  features_grid_h2_pre: string;
  features_grid_h2_highlight: string;
  features_grid_body: string;
  features_grid_items: Array<{ title: string; description: string }>;
  // Pricing
  pricing_badge: string;
  pricing_h2_pre: string;
  pricing_h2_highlight: string;
  pricing_body_wedding: string;
  pricing_body_anniversary: string;
  pricing_wedding_features: string[];
  pricing_anniversary_features: string[];
  pricing_anniversary_not_included: string[];
  pricing_anniversary_wedding_link: string;
  pricing_cta: string;
  pricing_planner_heading: string;
  pricing_planner_subheading: string;
  pricing_planner_features: string[];
  pricing_planner_cta: string;
  pricing_bespoke_pre: string;
  pricing_bespoke_link: string;
  pricing_most_popular: string;
  pricing_one_off: string;
  pricing_one_time_payment: string;
  // Testimonials
  testimonials_badge: string;
  testimonials_heading_pre: string;
  testimonials_heading_highlight: string;
  // OurStory
  our_story_badge: string;
  our_story_h2_pre: string;
  our_story_h2_highlight: string;
  our_story_body: string;
  // WeddingPlanner
  wedding_planner_h2_pre: string;
  wedding_planner_h2_highlight: string;
  wedding_planner_h2_post: string;
  wedding_planner_body: string;
  wedding_planner_features: Array<{ title: string; description: string }>;
  wedding_planner_cta: string;
  // SeatingPlanner
  seating_planner_badge: string;
  seating_planner_h2: string;
  seating_planner_body: string;
  seating_planner_bullets: string[];
  seating_planner_cta_link: string;
  seating_planner_price_label: string;
  // ThemeDemos
  theme_demos_badge: string;
  theme_demos_heading_pre: string;
  theme_demos_heading_highlight: string;
  theme_demos_subheading: string;
  theme_demos_cta: string;
  // Templates
  templates_badge: string;
  templates_h2_pre: string;
  templates_h2_highlight: string;
  templates_body: string;
  templates_cta: string;
  // Footer
  footer_cta_h2: string;
  footer_cta_body: string;
  footer_cta_primary: string;
  footer_cta_login: string;
  footer_brand_blurb: string;
  footer_contact_link: string;
  footer_col_invitations: string;
  footer_col_tools: string;
  footer_col_company: string;
  footer_copyright: string;
}

export const LOCALE_STRINGS: Record<string, HomepageStrings> = {
  en: {
    hero_h1_main: 'Your wedding.',
    hero_h1_highlight: 'Beautifully invited.',
    hero_subtitle:
      'Stunning digital invitations your guests will actually love — with built-in RSVP tracking, guest management, and everything you need to keep your big day organised.',
    hero_cta_primary: 'Create Your Invite',
    hero_cta_secondary: 'See How It Works',
    hero_social_1: 'Loved by couples on their big day',
    hero_social_2: 'Unlimited guests included',
    hero_social_3: 'Day & evening guest support',
    nav_features: 'Features',
    nav_themes: 'Themes',
    nav_how_it_works: 'How it Works',
    nav_pricing: 'Pricing',
    nav_faq: 'FAQ',
    nav_login: 'Log in',
    nav_cta: 'Create Invite',
    nav_app: 'Go to App',
    faq_heading: 'Frequently asked',
    faq_heading_highlight: 'questions',
    faq_subtitle: "Have questions? We've got answers. Can't find what you're looking for?",
    faq_contact_link: 'Get in touch',
    faq_contact_suffix: '.',
    emotive_statements: [
      {
        heading: 'Your love story deserves more than a group chat',
        highlight: 'group chat',
        copy: "Every couple's journey is unique. Your invitation should feel just as personal — a beautiful first glimpse of the day you've been dreaming about.",
      },
      {
        heading: 'First impressions that set the tone',
        highlight: 'set the tone',
        copy: "Your invite is the very first thing your guests see. Make it something they'll talk about — something that builds excitement from the moment they open it.",
      },
      {
        heading: 'Less admin, more anticipation',
        highlight: 'anticipation',
        copy: "Spend your time choosing flowers, not chasing RSVPs. Let your invite handle the details while you focus on what actually matters.",
      },
    ],
    faq_items: [
      {
        question: 'Do my guests need to download an app?',
        answer:
          'No app required. Guests open your invite link in any browser on any device — iPhone, Android, or desktop. They can RSVP instantly without downloading anything or creating an account.',
      },
      {
        question: 'Can I invite day guests and evening guests separately?',
        answer:
          'Yes. Tag guests as evening-only and they\'ll see the right schedule and details automatically. Day guests see the full day. One invite handles both.',
      },
      {
        question: 'How do guests RSVP?',
        answer:
          'Guests tap your invite link, view the invitation, and respond directly. No app to download, no account needed. You see their response instantly on your dashboard.',
      },
      {
        question: 'Can I update my invite after sending it?',
        answer:
          'Yes. Your invite is live — update any detail at any time and guests will always see the latest version. No need to resend.',
      },
      {
        question: 'Can I see a preview before I pay?',
        answer:
          "Yes. Browse our templates, see the animations, and explore the features. You only pay when you're ready to publish.",
      },
    ],
    how_it_works_badge: 'How It Works',
    how_it_works_h2_pre: 'Ready in ',
    how_it_works_h2_highlight: 'minutes',
    how_it_works_h2_post: ', not months',
    how_it_works_body: 'From browsing to sending, your wedding invitations are just four simple steps away.',
    how_it_works_steps: [
      { title: 'Pick your template', description: 'Browse our gallery and choose a design that feels like you.' },
      { title: 'Add your details', description: 'Our wizard walks you through names, dates, venues, and more. Done in minutes.' },
      { title: 'Add your guests', description: 'Import or add your guest list. Tag evening-only guests so they see the right info.' },
      { title: 'Share & track', description: 'Send your invite link and watch RSVPs roll in on your dashboard.' },
    ],
    headline_features_badge: 'Features',
    headline_features_h2_pre: 'Everything you need, ',
    headline_features_h2_highlight: "nothing you don't",
    headline_features_body: 'Every feature is included. No add-ons, no tiers, no per-guest fees.',
    headline_feature_blocks: [
      { heading_pre: 'Make it ', heading_highlight: 'yours', heading_post: '', copy: 'Every detail is in your hands. Add your names, dates, venues, schedule and photos in minutes with our simple wizard. Show or hide sections based on guest type, and update anything at any time. Your invite is live, so your guests always see the latest version.', cta: 'See How It Works' },
      { heading_pre: 'Your guest list, completely ', heading_highlight: 'sorted', heading_post: '', copy: 'Wedsy is your guest management hub. See RSVPs in real time, track who\'s opened their invite, manage day and evening guests, handle plus ones and dietary requirements, and chase up anyone who hasn\'t replied. All from one dashboard.', cta: 'Create Your Invite' },
      { heading_pre: 'RSVPs and reminders, ', heading_highlight: 'handled', heading_post: '', copy: 'Real-time RSVP tracking means you always know where you stand. Guests respond in one tap, dietary needs are collected automatically, and anyone who hasn\'t replied gets a gentle nudge straight from your dashboard.', cta: 'Create Your Invite' },
      { heading_pre: 'Save the Date, ', heading_highlight: 'included', heading_post: '', copy: 'Start with a Save the Date — it\'s a simple toggle in your invite settings. When you\'re ready to reveal the full details, just switch it to your full invite. More sections unlock, your design stays exactly the same, and there\'s nothing more to pay.', cta: 'Create Your Invite' },
      { heading_pre: "A wax seal reveal they'll ", heading_highlight: 'never forget', heading_post: '', copy: 'Every invite opens with an animated wax-sealed envelope — a moment of anticipation before guests see a single word. Choose from 18 envelope colour styles, each with a matching wax seal, and add it to your invite in one click.', cta: 'Create Your Invite' },
    ],
    features_grid_badge: 'More Features',
    features_grid_h2_pre: 'Powerful tools for ',
    features_grid_h2_highlight: 'every detail',
    features_grid_body: 'Guest management, tracking, reminders and more. All included, no extras.',
    features_grid_items: [
      { title: 'Day & Evening Guests', description: "One invite handles both. Evening guests only see what's relevant to them." },
      { title: 'Your Link, Your Way', description: "Customise your link, then track exactly who's viewed and responded." },
      { title: 'Custom Questions', description: 'Ask guests anything — add your own questions to the RSVP form.' },
      { title: 'RSVP Reminders', description: "Nudge guests who haven't responded yet — one tap from your dashboard." },
      { title: 'Share via Link, WhatsApp & QR', description: "Send it however your guests will actually see it. No app needed." },
      { title: 'Save the Date + Invitation', description: 'Two invite types included. Send your Save the Date first, then your full invitation.' },
      { title: 'Dietary Requirements', description: 'Collect dietary needs as part of the RSVP.' },
      { title: 'Guest Grouping & Tagging', description: 'Organise guests into groups and manage them by tag.' },
      { title: 'View Tracking', description: "See who's opened their invite and who hasn't looked yet." },
      { title: 'Multiple Venues', description: 'Ceremony in one place, reception in another? No problem.' },
      { title: 'Invite Collaborators', description: 'Invite your partner, planner, or anyone else to help manage your invites.' },
      { title: 'Live Invites', description: 'Update your invite anytime. Guests always see the latest — no need to resend.' },
      { title: 'Unlimited Guests', description: 'Invite as many people as you want. No caps, no extra charges.' },
      { title: 'Plus-One Controls', description: 'Set plus-one limits per guest. Collect names and dietary info for each additional guest.' },
      { title: 'Email Sending', description: 'Send invite links directly via email from your dashboard. No copy-pasting needed.' },
      { title: 'Show & Hide by Tag', description: 'Control which sections each guest group sees. Evening guests skip the ceremony details.' },
      { title: 'Public & Private Invites', description: 'Choose between a single shareable link or individual tracked links for each guest.' },
      { title: 'Schedule & Timeline', description: 'Add a full timeline of the day so guests know exactly what to expect and when.' },
      { title: 'CSV Import & Export', description: 'Import your guest list from a spreadsheet or export your data anytime.' },
      { title: 'Multi-Language Invites', description: 'Write your invite in as many languages as you need. Guests can select their language when they open it.' },
    ],
    pricing_badge: 'Pricing',
    pricing_h2_pre: 'Simple pricing, ',
    pricing_h2_highlight: 'no hidden fees',
    pricing_body_wedding: "One simple price per invite. Save the Date, day & night guests — all included. Design for free, pay when you're ready.",
    pricing_body_anniversary: "One price covers everything. Online RSVP, unlimited guests, all designs included. Design for free, pay when you're ready.",
    pricing_wedding_features: [
      'Save the Date included',
      'Unlimited guests',
      'Fully customisable',
      '5 stunning templates',
      'RSVP tracking & reminders',
      "See who's opened their invite",
      'Dietary requirements',
      'Plus-one controls',
      'Organise guests with tags',
      'Private & public invites',
      'Send via email or public link',
      'QR code available',
    ],
    pricing_anniversary_features: [
      'Online RSVP',
      'Unlimited guests',
      'Fully customisable',
      '5 stunning templates',
      'RSVP tracking & reminders',
      "See who's opened their invite",
      'Dietary requirements',
      'Plus-one controls',
      'Organise guests with tags',
      'Private & public invites',
      'Send via email or public link',
      'QR code available',
    ],
    pricing_anniversary_not_included: [
      'Save the Date',
      'Day & evening guest split',
      'Multiple venues',
    ],
    pricing_anniversary_wedding_link: 'See our wedding invitations from £39',
    pricing_cta: 'Create Your Invite',
    pricing_planner_heading: 'Planner',
    pricing_planner_subheading: 'For professional event planners',
    pricing_planner_features: [
      'Everything in the standard plan',
      'Up to 10 live invites at once',
      'Build unlimited draft invites',
      'Import guest lists via CSV',
      'Export guests, meal choices & dietary info to CSV',
      'Invite couples to manage their own invite',
      'Priority support',
    ],
    pricing_planner_cta: 'Get Started',
    pricing_bespoke_pre: 'Looking for something custom?',
    pricing_bespoke_link: 'Contact us about a bespoke design',
    pricing_most_popular: 'Most Popular',
    pricing_one_off: 'One-off',
    pricing_one_time_payment: 'One-time payment',
    testimonials_badge: 'Reviews',
    testimonials_heading_pre: 'Loved by couples ',
    testimonials_heading_highlight: 'on their big day',
    our_story_badge: 'Our Story',
    our_story_h2_pre: 'Built by a couple, ',
    our_story_h2_highlight: 'for couples',
    our_story_body: "When we started planning our own wedding, we were frustrated by expensive invitation platforms with hidden fees and clunky guest management tools. We wanted something beautiful, simple, and honest — so we built it ourselves. Wedsy is everything we wish we'd had: stunning designs, powerful features, and one fair price. No surprises, no per-guest charges, just the tools you need to celebrate your day your way.",
    wedding_planner_h2_pre: 'Are you a ',
    wedding_planner_h2_highlight: 'wedding planner',
    wedding_planner_h2_post: ' or venue?',
    wedding_planner_body: 'Manage up to 10 active invites with professional-grade tools. One simple price, no hidden costs.',
    wedding_planner_features: [
      { title: 'Up to 10 Active Invites', description: '£29/month subscription. Manage up to 10 active invites, unlimited guests. No per-guest fees.' },
      { title: 'Bulk Operations', description: 'Select multiple guests and perform actions in one click — send emails, change RSVPs, or reassign invites.' },
      { title: 'Show & Hide by Tag', description: 'Control which sections each guest group sees. Evening guests skip ceremony details.' },
      { title: 'Dietary Requirements', description: 'Collect dietary needs as part of the RSVP — all tracked automatically in your dashboard.' },
      { title: 'CSV Import & Export', description: 'Import guest lists from spreadsheets or export all your data anytime.' },
      { title: 'Full Customization', description: 'Choose from 32 font pairings and dozens of color palettes. Brand every invite perfectly.' },
    ],
    wedding_planner_cta: 'Get Started',
    seating_planner_badge: 'Seating Planner',
    seating_planner_h2: 'Plan every seat.\nAll in one place.',
    seating_planner_body: 'Design your room layout on an interactive canvas, drag guests onto seats, and export a beautiful PDF — everything your venue needs, done before the wedding.',
    seating_planner_bullets: [
      'Drag tables anywhere — round, rectangular or square',
      'Assign guests to seats with drag-and-drop',
      'Dietary and allergy flags visible at a glance',
      'Export a print-ready PDF for your venue',
    ],
    seating_planner_cta_link: 'Learn about Seating Planner',
    seating_planner_price_label: '· One-time add-on',
    templates_badge: 'Build Bespoke',
    templates_h2_pre: 'Designs your guests will ',
    templates_h2_highlight: 'talk about',
    templates_body: 'Animated, elegant invitation templates crafted for modern couples. Every template is designed to feel personal and premium.',
    theme_demos_badge: 'Theme Demos',
    theme_demos_heading_pre: 'See them ',
    theme_demos_heading_highlight: 'live',
    theme_demos_subheading: 'Every theme is a real invite — tap to explore it as your guests will.',
    theme_demos_cta: 'Create Your Invite',
    templates_cta: 'Create Your Invite',
    footer_cta_h2: 'Ready to create your invite?',
    footer_cta_body: 'Beautiful digital invitations with RSVP tracking and guest management. Everything included, one simple price.',
    footer_cta_primary: 'Create Your Invite',
    footer_cta_login: 'Log in',
    footer_brand_blurb: 'Beautiful digital wedding invitations with built-in RSVP tracking and guest management.',
    footer_contact_link: 'Send us a message',
    footer_col_invitations: 'Invitations',
    footer_col_tools: 'Tools',
    footer_col_company: 'Company',
    footer_copyright: 'All rights reserved.',
  },

  fr: {
    hero_h1_main: 'Votre mariage.',
    hero_h1_highlight: 'Sublimement invité.',
    hero_subtitle:
      "Des faire-parts numériques que vos invités vont vraiment adorer — avec suivi des RSVP, gestion des invités et tout ce qu'il vous faut pour que votre grand jour se déroule à la perfection.",
    hero_cta_primary: 'Créer votre faire-part',
    hero_cta_secondary: 'Voir comment ça marche',
    hero_social_1: 'Adoré par les mariés le jour J',
    hero_social_2: 'Invités illimités inclus',
    hero_social_3: 'Invités de journée & de soirée',
    nav_features: 'Fonctionnalités',
    nav_themes: 'Thèmes',
    nav_how_it_works: 'Comment ça marche',
    nav_pricing: 'Tarifs',
    nav_faq: 'FAQ',
    nav_login: 'Se connecter',
    nav_cta: 'Créer un faire-part',
    nav_app: 'Accéder à l\'app',
    faq_heading: 'Questions',
    faq_heading_highlight: 'fréquentes',
    faq_subtitle: 'Des questions ? Nous avons les réponses. Vous ne trouvez pas ce que vous cherchez ?',
    faq_contact_link: 'Contactez-nous',
    faq_contact_suffix: '.',
    emotive_statements: [
      {
        heading: 'Votre histoire mérite mieux qu\'un groupe WhatsApp',
        highlight: 'groupe WhatsApp',
        copy: "Chaque couple est unique. Votre faire-part devrait l'être aussi — un premier aperçu magnifique du jour dont vous rêvez.",
      },
      {
        heading: 'La première impression donne le ton',
        highlight: 'donne le ton',
        copy: "Votre faire-part est la toute première chose que verront vos invités. Faites-en quelque chose dont ils parleront — quelque chose qui éveille l'impatience dès l'instant où ils l'ouvrent.",
      },
      {
        heading: 'Moins de gestion, plus d\'impatience',
        highlight: "d'impatience",
        copy: "Consacrez votre temps au choix des fleurs, pas à courir après les RSVP. Laissez votre faire-part gérer les détails pendant que vous vous concentrez sur l'essentiel.",
      },
    ],
    faq_items: [
      {
        question: 'Mes invités doivent-ils télécharger une application ?',
        answer:
          "Aucune application requise. Les invités ouvrent le lien de votre faire-part dans n'importe quel navigateur, sur n'importe quel appareil — iPhone, Android ou ordinateur. Ils peuvent répondre instantanément sans rien télécharger ni créer de compte.",
      },
      {
        question: 'Puis-je inviter séparément les invités de journée et de soirée ?',
        answer:
          "Oui. Identifiez les invités comme invités du soir uniquement et ils verront automatiquement le bon programme et les bonnes informations. Les invités de journée voient le programme complet. Un seul faire-part gère les deux.",
      },
      {
        question: 'Comment les invités répondent-ils ?',
        answer:
          "Les invités cliquent sur votre lien, consultent le faire-part et répondent directement. Aucune application à télécharger, aucun compte à créer. Vous voyez leur réponse instantanément sur votre tableau de bord.",
      },
      {
        question: 'Puis-je modifier mon faire-part après l\'envoi ?',
        answer:
          "Oui. Votre faire-part est en ligne — mettez à jour n'importe quel détail à tout moment et les invités verront toujours la dernière version. Pas besoin de renvoyer.",
      },
      {
        question: 'Puis-je voir un aperçu avant de payer ?',
        answer:
          "Oui. Parcourez nos modèles, découvrez les animations et explorez les fonctionnalités. Vous ne payez que lorsque vous êtes prêt à publier.",
      },
    ],
    how_it_works_badge: 'Comment ça marche',
    how_it_works_h2_pre: 'Prêt en ',
    how_it_works_h2_highlight: 'quelques minutes',
    how_it_works_h2_post: ', pas en mois',
    how_it_works_body: 'De la sélection à l\'envoi, vos faire-parts de mariage ne sont qu\'à quatre étapes simples.',
    how_it_works_steps: [
      { title: 'Choisissez votre modèle', description: 'Parcourez notre galerie et choisissez un design qui vous ressemble.' },
      { title: 'Ajoutez vos informations', description: 'Notre assistant vous guide à travers les noms, dates, lieux et plus encore. Fait en quelques minutes.' },
      { title: 'Ajoutez vos invités', description: 'Importez ou ajoutez votre liste d\'invités. Identifiez les invités du soir pour qu\'ils voient les bonnes informations.' },
      { title: 'Partagez & suivez', description: 'Envoyez votre lien d\'invitation et regardez les RSVP affluer sur votre tableau de bord.' },
    ],
    headline_features_badge: 'Fonctionnalités',
    headline_features_h2_pre: 'Tout ce qu\'il vous faut, ',
    headline_features_h2_highlight: 'rien de superflu',
    headline_features_body: 'Toutes les fonctionnalités sont incluses. Pas de modules complémentaires, pas de niveaux, pas de frais par invité.',
    headline_feature_blocks: [
      { heading_pre: 'Faites-en ', heading_highlight: 'le vôtre', heading_post: '', copy: 'Chaque détail est entre vos mains. Ajoutez vos noms, dates, lieux, programme et photos en quelques minutes grâce à notre assistant. Affichez ou masquez des sections selon le type d\'invité, et modifiez tout à tout moment. Votre faire-part est en ligne, vos invités voient toujours la dernière version.', cta: 'Voir comment ça marche' },
      { heading_pre: 'Votre liste d\'invités, parfaitement ', heading_highlight: 'organisée', heading_post: '', copy: 'Wedsy est votre hub de gestion des invités. Suivez les RSVP en temps réel, repérez qui a ouvert le faire-part, gérez les invités de jour et de soirée, les accompagnateurs et les régimes alimentaires, et relancez ceux qui n\'ont pas répondu. Le tout depuis un seul tableau de bord.', cta: 'Créer votre faire-part' },
      { heading_pre: 'RSVP et rappels, ', heading_highlight: 'pris en charge', heading_post: '', copy: 'Le suivi RSVP en temps réel vous permet de toujours savoir où vous en êtes. Les invités répondent en un clic, les besoins alimentaires sont collectés automatiquement, et ceux qui n\'ont pas répondu reçoivent un rappel discret directement depuis votre tableau de bord.', cta: 'Créer votre faire-part' },
      { heading_pre: 'Save the Date, ', heading_highlight: 'inclus', heading_post: '', copy: 'Commencez par un Save the Date — c\'est simplement une option dans vos réglages. Quand vous êtes prêt à dévoiler tous les détails, basculez sur votre faire-part complet. Plus de sections se débloquent, le design reste identique, et rien de plus à payer.', cta: 'Créer votre faire-part' },
      { heading_pre: 'Une ouverture en cire dont ils se ', heading_highlight: 'souviendront', heading_post: '', copy: 'Chaque faire-part s\'ouvre avec une enveloppe animée à cachet de cire — un moment de suspense avant que vos invités ne lisent un seul mot. Choisissez parmi 18 styles d\'enveloppes, chacune avec son propre cachet, et ajoutez-le en un clic.', cta: 'Créer votre faire-part' },
    ],
    features_grid_badge: 'Plus de fonctionnalités',
    features_grid_h2_pre: 'Des outils puissants pour ',
    features_grid_h2_highlight: 'chaque détail',
    features_grid_body: 'Gestion des invités, suivi, rappels et plus encore. Tout inclus, sans supplément.',
    features_grid_items: [
      { title: 'Invités de jour & de soirée', description: 'Un seul faire-part gère les deux. Les invités du soir ne voient que ce qui les concerne.' },
      { title: 'Votre lien, à votre image', description: 'Personnalisez votre lien et suivez précisément qui l\'a consulté et qui a répondu.' },
      { title: 'Questions personnalisées', description: 'Demandez ce que vous souhaitez — ajoutez vos propres questions au formulaire RSVP.' },
      { title: 'Rappels RSVP', description: 'Relancez les invités qui n\'ont pas encore répondu — en un clic depuis votre tableau de bord.' },
      { title: 'Partage par lien, WhatsApp & QR', description: 'Envoyez-le comme vos invités le verront vraiment. Pas d\'application nécessaire.' },
      { title: 'Save the Date + Faire-part', description: 'Deux types de faire-parts inclus. Envoyez d\'abord votre Save the Date, puis votre faire-part complet.' },
      { title: 'Régimes alimentaires', description: 'Collectez les besoins alimentaires dans le cadre du RSVP.' },
      { title: 'Groupes & étiquettes d\'invités', description: 'Organisez les invités en groupes et gérez-les par étiquette.' },
      { title: 'Suivi des consultations', description: 'Voyez qui a ouvert son faire-part et qui ne l\'a pas encore consulté.' },
      { title: 'Plusieurs lieux', description: 'Cérémonie à un endroit, réception à un autre ? Aucun problème.' },
      { title: 'Inviter des collaborateurs', description: 'Invitez votre partenaire, votre wedding planner ou toute autre personne à vous aider.' },
      { title: 'Faire-parts en ligne', description: 'Modifiez votre faire-part à tout moment. Vos invités voient toujours la dernière version.' },
      { title: 'Invités illimités', description: 'Invitez autant de personnes que vous le souhaitez. Aucune limite, aucun frais supplémentaire.' },
      { title: 'Contrôle des accompagnateurs', description: 'Définissez le nombre d\'accompagnateurs par invité. Collectez leurs noms et régimes alimentaires.' },
      { title: 'Envoi par e-mail', description: 'Envoyez les liens d\'invitation directement par e-mail depuis votre tableau de bord. Sans copier-coller.' },
      { title: 'Afficher & masquer par étiquette', description: 'Contrôlez quelles sections chaque groupe voit. Les invités du soir ne voient pas les détails de la cérémonie.' },
      { title: 'Faire-parts publics & privés', description: 'Choisissez entre un lien partageable unique ou des liens individuels suivis pour chaque invité.' },
      { title: 'Programme & calendrier', description: 'Ajoutez un programme complet pour que vos invités sachent exactement ce qui les attend et quand.' },
      { title: 'Import & export CSV', description: 'Importez votre liste d\'invités depuis un tableur ou exportez vos données à tout moment.' },
      { title: 'Faire-parts multilingues', description: 'Rédigez votre faire-part en autant de langues que nécessaire. Les invités choisissent leur langue à l\'ouverture.' },
    ],
    pricing_badge: 'Tarifs',
    pricing_h2_pre: 'Tarif simple, ',
    pricing_h2_highlight: 'sans frais cachés',
    pricing_body_wedding: 'Un prix unique par faire-part. Save the Date, invités de jour et de soirée — tout inclus. Concevez gratuitement, payez quand vous êtes prêt.',
    pricing_body_anniversary: 'Un prix unique qui couvre tout. RSVP en ligne, invités illimités, tous les designs inclus. Concevez gratuitement, payez quand vous êtes prêt.',
    pricing_wedding_features: [
      'Save the Date inclus',
      'Invités illimités',
      'Entièrement personnalisable',
      '5 modèles élégants',
      'Suivi RSVP & rappels',
      "Voir qui a ouvert le faire-part",
      'Régimes alimentaires',
      'Contrôle des accompagnateurs',
      'Organisez les invités avec des étiquettes',
      'Faire-parts privés & publics',
      'Envoi par e-mail ou lien public',
      'QR code disponible',
    ],
    pricing_anniversary_features: [
      'RSVP en ligne',
      'Invités illimités',
      'Entièrement personnalisable',
      '5 modèles élégants',
      'Suivi RSVP & rappels',
      "Voir qui a ouvert le faire-part",
      'Régimes alimentaires',
      'Contrôle des accompagnateurs',
      'Organisez les invités avec des étiquettes',
      'Faire-parts privés & publics',
      'Envoi par e-mail ou lien public',
      'QR code disponible',
    ],
    pricing_anniversary_not_included: [
      'Save the Date',
      'Invités de jour & de soirée',
      'Plusieurs lieux',
    ],
    pricing_anniversary_wedding_link: 'Voir nos faire-parts de mariage à partir de £39',
    pricing_cta: 'Créer votre faire-part',
    pricing_planner_heading: 'Organisateur',
    pricing_planner_subheading: 'Pour les wedding planners professionnels',
    pricing_planner_features: [
      'Tout inclus dans le plan standard',
      "Jusqu'à 10 faire-parts actifs simultanément",
      'Créez des brouillons illimités',
      'Importez les listes d\'invités via CSV',
      'Exportez invités, choix de repas & régimes en CSV',
      'Invitez les couples à gérer leur faire-part',
      'Support prioritaire',
    ],
    pricing_planner_cta: 'Commencer',
    pricing_bespoke_pre: 'Vous cherchez quelque chose de sur-mesure ?',
    pricing_bespoke_link: 'Contactez-nous pour un design personnalisé',
    pricing_most_popular: 'Le plus populaire',
    pricing_one_off: 'Achat unique',
    pricing_one_time_payment: 'Paiement unique',
    testimonials_badge: 'Avis',
    testimonials_heading_pre: 'Apprécié par les couples ',
    testimonials_heading_highlight: 'le jour de leur mariage',
    our_story_badge: 'Notre histoire',
    our_story_h2_pre: 'Créé par un couple, ',
    our_story_h2_highlight: 'pour les couples',
    our_story_body: "Lorsque nous avons commencé à planifier notre propre mariage, nous étions frustrés par des plateformes d'invitations coûteuses, avec des frais cachés et des outils de gestion des invités peu pratiques. Nous voulions quelque chose de beau, simple et honnête — alors nous l'avons créé nous-mêmes. Wedsy, c'est tout ce que nous aurions aimé avoir : des designs élégants, des fonctionnalités puissantes et un prix juste. Sans surprises, sans frais par invité, juste les outils pour célébrer votre journée à votre façon.",
    wedding_planner_h2_pre: 'Êtes-vous ',
    wedding_planner_h2_highlight: 'wedding planner',
    wedding_planner_h2_post: ' ou propriétaire de lieu ?',
    wedding_planner_body: 'Gérez jusqu\'à 10 faire-parts actifs avec des outils de niveau professionnel. Un prix simple, sans frais cachés.',
    wedding_planner_features: [
      { title: "Jusqu'à 10 faire-parts actifs", description: 'Abonnement à £29/mois. Gérez jusqu\'à 10 faire-parts actifs, invités illimités. Sans frais par invité.' },
      { title: 'Opérations en masse', description: 'Sélectionnez plusieurs invités et effectuez des actions en un clic — envoi d\'e-mails, modification des RSVP ou réaffectation.' },
      { title: 'Afficher & masquer par étiquette', description: 'Contrôlez quelles sections chaque groupe voit. Les invités du soir ne voient pas les détails de la cérémonie.' },
      { title: 'Régimes alimentaires', description: 'Collectez les besoins alimentaires dans le cadre du RSVP — tout est suivi automatiquement dans votre tableau de bord.' },
      { title: 'Import & export CSV', description: 'Importez les listes d\'invités depuis des tableurs ou exportez toutes vos données à tout moment.' },
      { title: 'Personnalisation complète', description: 'Choisissez parmi 32 associations de polices et des dizaines de palettes de couleurs. Chaque faire-part à votre image.' },
    ],
    wedding_planner_cta: 'Commencer',
    seating_planner_badge: 'Plan de table',
    seating_planner_h2: 'Planifiez chaque place.\nEn un seul endroit.',
    seating_planner_body: 'Concevez le plan de votre salle sur un canevas interactif, placez vos invités sur leurs sièges et exportez un beau PDF — tout ce dont votre lieu a besoin, prêt avant le mariage.',
    seating_planner_bullets: [
      'Déplacez les tables librement — rondes, rectangulaires ou carrées',
      'Assignez les invités à leurs places par glisser-déposer',
      'Régimes et allergies visibles d\'un coup d\'œil',
      'Exportez un PDF prêt à imprimer pour votre lieu de réception',
    ],
    seating_planner_cta_link: 'En savoir plus sur le plan de table',
    seating_planner_price_label: '· Supplément unique',
    templates_badge: 'Fait sur mesure',
    templates_h2_pre: 'Des designs dont vos invités ',
    templates_h2_highlight: 'parleront',
    templates_body: 'Des modèles d\'invitation animés et élégants, conçus pour les couples modernes. Chaque modèle est pensé pour être personnel et haut de gamme.',
    theme_demos_badge: 'Démos des thèmes',
    theme_demos_heading_pre: 'Découvrez-les ',
    theme_demos_heading_highlight: 'en direct',
    theme_demos_subheading: 'Chaque thème est une vraie invitation — appuyez pour l\'explorer comme vos invités.',
    theme_demos_cta: 'Créer votre faire-part',
    templates_cta: 'Créer votre faire-part',
    footer_cta_h2: 'Prêt à créer votre faire-part ?',
    footer_cta_body: 'De beaux faire-parts numériques avec suivi RSVP et gestion des invités. Tout inclus, un prix simple.',
    footer_cta_primary: 'Créer votre faire-part',
    footer_cta_login: 'Se connecter',
    footer_brand_blurb: 'De beaux faire-parts de mariage numériques avec suivi RSVP et gestion des invités intégrés.',
    footer_contact_link: 'Envoyez-nous un message',
    footer_col_invitations: 'Invitations',
    footer_col_tools: 'Outils',
    footer_col_company: 'Entreprise',
    footer_copyright: 'Tous droits réservés.',
  },

  de: {
    hero_h1_main: 'Eure Hochzeit.',
    hero_h1_highlight: 'Wunderschön eingeladen.',
    hero_subtitle:
      'Beeindruckende digitale Einladungen, die eure Gäste wirklich begeistern werden — mit integrierter RSVP-Verwaltung, Gästemanagement und allem, was ihr für einen reibungslosen großen Tag braucht.',
    hero_cta_primary: 'Einladung erstellen',
    hero_cta_secondary: 'So funktioniert es',
    hero_social_1: 'Geliebt von Paaren an ihrem großen Tag',
    hero_social_2: 'Unbegrenzte Gäste inklusive',
    hero_social_3: 'Unterstützung für Tages- & Abendgäste',
    nav_features: 'Funktionen',
    nav_themes: 'Designs',
    nav_how_it_works: 'So funktioniert es',
    nav_pricing: 'Preise',
    nav_faq: 'FAQ',
    nav_login: 'Anmelden',
    nav_cta: 'Einladung erstellen',
    nav_app: 'Zur App',
    faq_heading: 'Häufig gestellte',
    faq_heading_highlight: 'Fragen',
    faq_subtitle: 'Ihr habt Fragen? Wir haben Antworten. Findet ihr nicht, was ihr sucht?',
    faq_contact_link: 'Schreibt uns',
    faq_contact_suffix: '.',
    emotive_statements: [
      {
        heading: 'Eure Liebesgeschichte verdient mehr als eine Gruppenspricht',
        highlight: 'Gruppenspricht',
        copy: "Jedes Paar ist einzigartig. Eure Einladung sollte das genauso spiegeln — ein wunderschöner erster Einblick in den Tag, von dem ihr schon so lange träumt.",
      },
      {
        heading: 'Der erste Eindruck setzt den Ton',
        highlight: 'setzt den Ton',
        copy: "Eure Einladung ist das Allererste, was eure Gäste sehen. Macht daraus etwas, worüber sie sprechen werden — etwas, das die Vorfreude vom ersten Moment an weckt.",
      },
      {
        heading: 'Weniger Organisationsaufwand, mehr Vorfreude',
        highlight: 'Vorfreude',
        copy: "Verwendet eure Zeit lieber für die Blumenauswahl, nicht für das Hinterherlaufen nach RSVPs. Lasst eure Einladung die Details regeln, während ihr euch auf das Wesentliche konzentriert.",
      },
    ],
    faq_items: [
      {
        question: 'Müssen meine Gäste eine App herunterladen?',
        answer:
          'Keine App erforderlich. Gäste öffnen euren Einladungslink in jedem Browser auf jedem Gerät — iPhone, Android oder Desktop. Sie können sofort antworten, ohne etwas herunterzuladen oder ein Konto zu erstellen.',
      },
      {
        question: 'Kann ich Tagesgäste und Abendgäste getrennt einladen?',
        answer:
          'Ja. Markiert Gäste als reine Abendgäste und sie sehen automatisch den richtigen Zeitplan und die richtigen Details. Tagesgäste sehen den vollständigen Tag. Eine Einladung deckt beides ab.',
      },
      {
        question: 'Wie antworten die Gäste?',
        answer:
          'Gäste tippen auf euren Einladungslink, schauen sich die Einladung an und antworten direkt. Keine App zum Herunterladen, kein Konto nötig. Ihr seht ihre Antwort sofort in eurem Dashboard.',
      },
      {
        question: 'Kann ich meine Einladung nach dem Versand noch ändern?',
        answer:
          'Ja. Eure Einladung ist live — ändert jederzeit beliebige Details und eure Gäste sehen immer die neueste Version. Kein erneutes Versenden nötig.',
      },
      {
        question: 'Kann ich eine Vorschau sehen, bevor ich bezahle?',
        answer:
          'Ja. Stöbert durch unsere Vorlagen, schaut euch die Animationen an und entdeckt die Funktionen. Ihr zahlt nur, wenn ihr bereit seid zu veröffentlichen.',
      },
    ],
    how_it_works_badge: 'So funktioniert es',
    how_it_works_h2_pre: 'Fertig in ',
    how_it_works_h2_highlight: 'Minuten',
    how_it_works_h2_post: ', nicht Monaten',
    how_it_works_body: 'Von der Auswahl bis zum Versand — eure Hochzeitseinladungen sind nur vier einfache Schritte entfernt.',
    how_it_works_steps: [
      { title: 'Vorlage auswählen', description: 'Stöbert durch unsere Galerie und wählt ein Design, das zu euch passt.' },
      { title: 'Details hinzufügen', description: 'Unser Assistent führt euch durch Namen, Daten, Orte und mehr. In wenigen Minuten erledigt.' },
      { title: 'Gäste hinzufügen', description: 'Importiert oder fügt eure Gästeliste ein. Kennzeichnet Abendgäste, damit sie die richtigen Infos sehen.' },
      { title: 'Teilen & verfolgen', description: 'Sendet euren Einladungslink und beobachtet, wie die RSVPs in eurem Dashboard eintreffen.' },
    ],
    headline_features_badge: 'Funktionen',
    headline_features_h2_pre: 'Alles, was ihr braucht, ',
    headline_features_h2_highlight: 'nichts, was ihr nicht braucht',
    headline_features_body: 'Alle Funktionen sind inbegriffen. Keine Zusatzmodule, keine Stufen, keine Gebühren pro Gast.',
    headline_feature_blocks: [
      { heading_pre: 'Macht es zu ', heading_highlight: 'eurem', heading_post: '', copy: 'Jedes Detail liegt in eurer Hand. Fügt Namen, Daten, Orte, Ablauf und Fotos in wenigen Minuten mit unserem einfachen Assistenten hinzu. Zeigt oder verbergt Abschnitte je nach Gästetyp und ändert alles jederzeit. Eure Einladung ist live, sodass eure Gäste immer die neueste Version sehen.', cta: 'So funktioniert es' },
      { heading_pre: 'Eure Gästeliste, perfekt ', heading_highlight: 'organisiert', heading_post: '', copy: 'Wedsy ist euer Gästemanagement-Hub. Verfolgt RSVPs in Echtzeit, seht wer die Einladung geöffnet hat, verwaltet Tages- und Abendgäste, Begleitpersonen und Ernährungsanforderungen und erinnert alle, die noch nicht geantwortet haben. Alles von einem einzigen Dashboard aus.', cta: 'Einladung erstellen' },
      { heading_pre: 'RSVPs und Erinnerungen, ', heading_highlight: 'erledigt', heading_post: '', copy: 'Echtzeit-RSVP-Tracking bedeutet, dass ihr immer wisst, wie es steht. Gäste antworten mit einem Tippen, Ernährungsbedürfnisse werden automatisch erfasst, und alle, die noch nicht geantwortet haben, erhalten einen sanften Hinweis direkt von eurem Dashboard.', cta: 'Einladung erstellen' },
      { heading_pre: 'Save the Date, ', heading_highlight: 'inklusive', heading_post: '', copy: 'Beginnt mit einem Save the Date — es ist ein einfacher Schalter in euren Einladungseinstellungen. Wenn ihr bereit seid, alle Details zu enthüllen, wechselt einfach zur vollständigen Einladung. Mehr Abschnitte werden freigeschaltet, euer Design bleibt genau gleich, und es gibt nichts mehr zu bezahlen.', cta: 'Einladung erstellen' },
      { heading_pre: 'Ein Siegelwachs-Auftakt, den sie ', heading_highlight: 'nie vergessen', heading_post: '', copy: 'Jede Einladung öffnet sich mit einem animierten Wachssiegel-Umschlag — ein Moment der Vorfreude, bevor Gäste ein einziges Wort sehen. Wählt aus 18 Umschlagfarben, jede mit passendem Wachssiegel, und fügt es mit einem Klick hinzu.', cta: 'Einladung erstellen' },
    ],
    features_grid_badge: 'Weitere Funktionen',
    features_grid_h2_pre: 'Leistungsstarke Tools für ',
    features_grid_h2_highlight: 'jedes Detail',
    features_grid_body: 'Gästemanagement, Tracking, Erinnerungen und mehr. Alles inklusive, keine Extras.',
    features_grid_items: [
      { title: 'Tages- & Abendgäste', description: 'Eine Einladung deckt beides ab. Abendgäste sehen nur, was für sie relevant ist.' },
      { title: 'Euer Link, euer Stil', description: 'Personalisiert euren Link und verfolgt genau, wer ihn aufgerufen und geantwortet hat.' },
      { title: 'Individuelle Fragen', description: 'Fragt eure Gäste alles — fügt eigene Fragen zum RSVP-Formular hinzu.' },
      { title: 'RSVP-Erinnerungen', description: 'Erinnert Gäste, die noch nicht geantwortet haben — ein Tippen aus eurem Dashboard.' },
      { title: 'Teilen per Link, WhatsApp & QR', description: 'Sendet es so, wie eure Gäste es wirklich sehen werden. Keine App nötig.' },
      { title: 'Save the Date + Einladung', description: 'Zwei Einladungstypen inklusive. Sendet zuerst euer Save the Date, dann eure vollständige Einladung.' },
      { title: 'Ernährungsanforderungen', description: 'Erfasst Ernährungsbedürfnisse als Teil des RSVPs.' },
      { title: 'Gästegruppen & Tags', description: 'Organisiert Gäste in Gruppen und verwaltet sie nach Tags.' },
      { title: 'Aufrufverfolgung', description: 'Seht, wer die Einladung geöffnet hat und wer sie noch nicht angeschaut hat.' },
      { title: 'Mehrere Orte', description: 'Zeremonie an einem Ort, Empfang an einem anderen? Kein Problem.' },
      { title: 'Mitarbeiter einladen', description: 'Ladet euren Partner, Planer oder andere ein, bei der Verwaltung zu helfen.' },
      { title: 'Live-Einladungen', description: 'Aktualisiert eure Einladung jederzeit. Gäste sehen immer die neueste Version.' },
      { title: 'Unbegrenzte Gäste', description: 'Ladet so viele Personen ein, wie ihr möchtet. Keine Obergrenzen, keine Extragebühren.' },
      { title: 'Begleitpersonen-Kontrolle', description: 'Legt Limits für Begleitpersonen pro Gast fest. Erfasst Namen und Ernährungsangaben.' },
      { title: 'E-Mail-Versand', description: 'Sendet Einladungslinks direkt per E-Mail aus eurem Dashboard. Kein Kopieren und Einfügen.' },
      { title: 'Anzeigen & Ausblenden nach Tag', description: 'Kontrolliert, welche Abschnitte jede Gästegruppe sieht. Abendgäste überspringen die Zeremoniedetails.' },
      { title: 'Öffentliche & private Einladungen', description: 'Wählt zwischen einem einzelnen teilbaren Link oder individuell verfolgbaren Links für jeden Gast.' },
      { title: 'Ablauf & Zeitplan', description: 'Fügt einen vollständigen Tagesablauf hinzu, damit Gäste genau wissen, was sie erwartet und wann.' },
      { title: 'CSV-Import & Export', description: 'Importiert eure Gästeliste aus einer Tabelle oder exportiert eure Daten jederzeit.' },
      { title: 'Mehrsprachige Einladungen', description: 'Schreibt eure Einladung in so vielen Sprachen wie nötig. Gäste wählen ihre Sprache beim Öffnen.' },
    ],
    pricing_badge: 'Preise',
    pricing_h2_pre: 'Einfache Preise, ',
    pricing_h2_highlight: 'keine versteckten Kosten',
    pricing_body_wedding: 'Ein einfacher Preis pro Einladung. Save the Date, Tages- und Abendgäste — alles inklusive. Kostenlos gestalten, zahlen wenn ihr bereit seid.',
    pricing_body_anniversary: 'Ein Preis deckt alles ab. Online-RSVP, unbegrenzte Gäste, alle Designs inklusive. Kostenlos gestalten, zahlen wenn ihr bereit seid.',
    pricing_wedding_features: [
      'Save the Date inklusive',
      'Unbegrenzte Gäste',
      'Vollständig anpassbar',
      '5 elegante Vorlagen',
      'RSVP-Tracking & Erinnerungen',
      'Wer hat die Einladung geöffnet?',
      'Ernährungsanforderungen',
      'Begleitpersonen-Kontrolle',
      'Gäste mit Tags organisieren',
      'Private & öffentliche Einladungen',
      'Versand per E-Mail oder öffentlichem Link',
      'QR-Code verfügbar',
    ],
    pricing_anniversary_features: [
      'Online-RSVP',
      'Unbegrenzte Gäste',
      'Vollständig anpassbar',
      '5 elegante Vorlagen',
      'RSVP-Tracking & Erinnerungen',
      'Wer hat die Einladung geöffnet?',
      'Ernährungsanforderungen',
      'Begleitpersonen-Kontrolle',
      'Gäste mit Tags organisieren',
      'Private & öffentliche Einladungen',
      'Versand per E-Mail oder öffentlichem Link',
      'QR-Code verfügbar',
    ],
    pricing_anniversary_not_included: [
      'Save the Date',
      'Tages- & Abendgäste-Trennung',
      'Mehrere Orte',
    ],
    pricing_anniversary_wedding_link: 'Unsere Hochzeitseinladungen ab £39 ansehen',
    pricing_cta: 'Einladung erstellen',
    pricing_planner_heading: 'Planer',
    pricing_planner_subheading: 'Für professionelle Event-Planer',
    pricing_planner_features: [
      'Alles im Standardplan inklusive',
      'Bis zu 10 aktive Einladungen gleichzeitig',
      'Unbegrenzte Entwürfe erstellen',
      'Gästelisten per CSV importieren',
      'Gäste, Menüwahl & Ernährungsinfos als CSV exportieren',
      'Paare zur Verwaltung ihrer Einladung einladen',
      'Prioritätssupport',
    ],
    pricing_planner_cta: 'Loslegen',
    pricing_bespoke_pre: 'Sucht ihr etwas Individuelles?',
    pricing_bespoke_link: 'Kontaktiert uns für ein maßgeschneidertes Design',
    pricing_most_popular: 'Beliebteste Wahl',
    pricing_one_off: 'Einmalig',
    pricing_one_time_payment: 'Einmalzahlung',
    testimonials_badge: 'Bewertungen',
    testimonials_heading_pre: 'Geliebt von Paaren ',
    testimonials_heading_highlight: 'an ihrem großen Tag',
    our_story_badge: 'Unsere Geschichte',
    our_story_h2_pre: 'Von einem Paar gebaut, ',
    our_story_h2_highlight: 'für Paare',
    our_story_body: 'Als wir anfingen, unsere eigene Hochzeit zu planen, waren wir frustriert von teuren Einladungsplattformen mit versteckten Gebühren und umständlichen Gästemanagement-Tools. Wir wollten etwas Schönes, Einfaches und Ehrliches — also haben wir es selbst gebaut. Wedsy ist alles, was wir uns gewünscht hätten: atemberaubende Designs, leistungsstarke Funktionen und ein fairer Preis. Keine Überraschungen, keine Gebühren pro Gast, nur die Tools, die ihr braucht, um euren besonderen Tag auf eure Weise zu feiern.',
    wedding_planner_h2_pre: 'Seid ihr ',
    wedding_planner_h2_highlight: 'Hochzeitsplaner',
    wedding_planner_h2_post: ' oder Eventlocation?',
    wedding_planner_body: 'Verwaltet bis zu 10 aktive Einladungen mit professionellen Tools. Ein einfacher Preis, keine versteckten Kosten.',
    wedding_planner_features: [
      { title: 'Bis zu 10 aktive Einladungen', description: '£29/Monat Abonnement. Bis zu 10 aktive Einladungen verwalten, unbegrenzte Gäste. Keine Gebühren pro Gast.' },
      { title: 'Massenaktionen', description: 'Mehrere Gäste auswählen und Aktionen mit einem Klick ausführen — E-Mails senden, RSVPs ändern oder Einladungen neu zuweisen.' },
      { title: 'Anzeigen & Ausblenden nach Tag', description: 'Kontrolliert, welche Abschnitte jede Gästegruppe sieht. Abendgäste überspringen Zeremoniedetails.' },
      { title: 'Ernährungsanforderungen', description: 'Erfasst Ernährungsbedürfnisse als Teil des RSVPs — alles wird automatisch in eurem Dashboard verfolgt.' },
      { title: 'CSV-Import & Export', description: 'Gästelisten aus Tabellen importieren oder alle Daten jederzeit exportieren.' },
      { title: 'Vollständige Anpassung', description: 'Wählt aus 32 Schriftkombinationen und Dutzenden von Farbpaletten. Jede Einladung perfekt gebrantet.' },
    ],
    wedding_planner_cta: 'Loslegen',
    seating_planner_badge: 'Sitzplatzplaner',
    seating_planner_h2: 'Jeden Platz planen.\nAlles an einem Ort.',
    seating_planner_body: 'Gestaltet euren Raumlayout auf einer interaktiven Leinwand, zieht Gäste auf ihre Plätze und exportiert ein schönes PDF — alles was euer Veranstaltungsort braucht, fertig vor der Hochzeit.',
    seating_planner_bullets: [
      'Tische frei platzieren — rund, rechteckig oder quadratisch',
      'Gäste per Drag-and-Drop auf Plätze zuweisen',
      'Ernährungs- und Allergiehinweise auf einen Blick',
      'Ein druckfertiges PDF für euren Veranstaltungsort exportieren',
    ],
    seating_planner_cta_link: 'Mehr über den Sitzplatzplaner',
    seating_planner_price_label: '· Einmalige Ergänzung',
    templates_badge: 'Individuell gestalten',
    templates_h2_pre: 'Designs, über die eure Gäste ',
    templates_h2_highlight: 'sprechen werden',
    templates_body: 'Animierte, elegante Einladungsvorlagen für moderne Paare. Jede Vorlage ist darauf ausgelegt, persönlich und hochwertig zu wirken.',
    theme_demos_badge: 'Theme-Demos',
    theme_demos_heading_pre: 'Seht sie ',
    theme_demos_heading_highlight: 'live',
    theme_demos_subheading: 'Jedes Theme ist eine echte Einladung — tippt darauf, um sie wie eure Gäste zu erleben.',
    theme_demos_cta: 'Einladung erstellen',
    templates_cta: 'Einladung erstellen',
    footer_cta_h2: 'Bereit, eure Einladung zu erstellen?',
    footer_cta_body: 'Schöne digitale Einladungen mit RSVP-Tracking und Gästemanagement. Alles inklusive, ein einfacher Preis.',
    footer_cta_primary: 'Einladung erstellen',
    footer_cta_login: 'Anmelden',
    footer_brand_blurb: 'Schöne digitale Hochzeitseinladungen mit integriertem RSVP-Tracking und Gästemanagement.',
    footer_contact_link: 'Schreibt uns',
    footer_col_invitations: 'Einladungen',
    footer_col_tools: 'Tools',
    footer_col_company: 'Unternehmen',
    footer_copyright: 'Alle Rechte vorbehalten.',
  },

  es: {
    hero_h1_main: 'Tu boda.',
    hero_h1_highlight: 'Invitada con elegancia.',
    hero_subtitle:
      'Invitaciones digitales impresionantes que tus invitados amarán de verdad — con seguimiento de confirmaciones, gestión de invitados y todo lo que necesitas para que tu gran día salga perfecto.',
    hero_cta_primary: 'Crear tu invitación',
    hero_cta_secondary: 'Ver cómo funciona',
    hero_social_1: 'Adorado por parejas en su gran día',
    hero_social_2: 'Invitados ilimitados incluidos',
    hero_social_3: 'Invitados de día y de noche',
    nav_features: 'Funciones',
    nav_themes: 'Diseños',
    nav_how_it_works: 'Cómo funciona',
    nav_pricing: 'Precios',
    nav_faq: 'Preguntas',
    nav_login: 'Iniciar sesión',
    nav_cta: 'Crear invitación',
    nav_app: 'Ir a la app',
    faq_heading: 'Preguntas',
    faq_heading_highlight: 'frecuentes',
    faq_subtitle: '¿Tienes preguntas? Nosotros tenemos las respuestas. ¿No encuentras lo que buscas?',
    faq_contact_link: 'Contáctanos',
    faq_contact_suffix: '.',
    emotive_statements: [
      {
        heading: 'Vuestra historia de amor merece más que un grupo de WhatsApp',
        highlight: 'grupo de WhatsApp',
        copy: "Cada pareja es única. Vuestra invitación debería serlo también — un primer vistazo precioso al día que habéis estado soñando.",
      },
      {
        heading: 'La primera impresión marca el tono',
        highlight: 'marca el tono',
        copy: "Vuestra invitación es lo primero que verán vuestros invitados. Haced que sea algo de lo que hablen — algo que despierte la emoción desde el primer momento en que la abran.",
      },
      {
        heading: 'Menos gestión, más ilusión',
        highlight: 'ilusión',
        copy: "Dedicad vuestro tiempo a elegir las flores, no a perseguir confirmaciones. Dejad que vuestra invitación gestione los detalles mientras vosotros os concentráis en lo que realmente importa.",
      },
    ],
    faq_items: [
      {
        question: '¿Mis invitados necesitan descargar una app?',
        answer:
          'No hace falta ninguna app. Los invitados abren el enlace de tu invitación en cualquier navegador y desde cualquier dispositivo — iPhone, Android u ordenador. Pueden confirmar asistencia al instante sin descargar nada ni crear una cuenta.',
      },
      {
        question: '¿Puedo invitar por separado a los invitados de día y de noche?',
        answer:
          'Sí. Etiqueta a los invitados como solo de noche y verán automáticamente el programa y los detalles correctos. Los invitados de día ven el día completo. Una sola invitación lo gestiona todo.',
      },
      {
        question: '¿Cómo confirman asistencia los invitados?',
        answer:
          'Los invitados tocan el enlace de la invitación, la visualizan y responden directamente. Sin app que descargar, sin cuenta que crear. Ves su respuesta al instante en tu panel de control.',
      },
      {
        question: '¿Puedo actualizar la invitación después de enviarla?',
        answer:
          'Sí. Tu invitación está en línea — actualiza cualquier detalle en cualquier momento y los invitados verán siempre la versión más reciente. No es necesario reenviar.',
      },
      {
        question: '¿Puedo ver una vista previa antes de pagar?',
        answer:
          'Sí. Explora nuestras plantillas, descubre las animaciones y conoce las funciones. Solo pagas cuando estás listo para publicar.',
      },
    ],
    how_it_works_badge: 'Cómo funciona',
    how_it_works_h2_pre: 'Listo en ',
    how_it_works_h2_highlight: 'minutos',
    how_it_works_h2_post: ', no en meses',
    how_it_works_body: 'Desde la selección hasta el envío, vuestra invitación de boda está a solo cuatro sencillos pasos.',
    how_it_works_steps: [
      { title: 'Elige tu plantilla', description: 'Explora nuestra galería y escoge un diseño que os represente.' },
      { title: 'Añade vuestros datos', description: 'Nuestro asistente os guía a través de nombres, fechas, lugares y más. Listo en minutos.' },
      { title: 'Añade a vuestros invitados', description: 'Importa o añade vuestra lista de invitados. Etiqueta a los invitados de noche para que vean la información correcta.' },
      { title: 'Comparte & haz seguimiento', description: 'Envía el enlace de la invitación y observa cómo llegan las confirmaciones a vuestro panel.' },
    ],
    headline_features_badge: 'Funciones',
    headline_features_h2_pre: 'Todo lo que necesitáis, ',
    headline_features_h2_highlight: 'nada que sobre',
    headline_features_body: 'Todas las funciones están incluidas. Sin complementos, sin niveles, sin cargos por invitado.',
    headline_feature_blocks: [
      { heading_pre: 'Hacedla ', heading_highlight: 'vuestra', heading_post: '', copy: 'Cada detalle está en vuestras manos. Añadid nombres, fechas, lugares, programa y fotos en minutos con nuestro sencillo asistente. Mostrad u ocultad secciones según el tipo de invitado, y actualizad cualquier cosa en cualquier momento. Vuestra invitación está en línea, para que los invitados vean siempre la última versión.', cta: 'Ver cómo funciona' },
      { heading_pre: 'Vuestra lista de invitados, perfectamente ', heading_highlight: 'organizada', heading_post: '', copy: 'Wedsy es vuestro centro de gestión de invitados. Seguid las confirmaciones en tiempo real, comprobad quién ha abierto la invitación, gestionad invitados de día y de noche, acompañantes y necesidades dietéticas, y recordad a quienes no han respondido. Todo desde un único panel.', cta: 'Crear tu invitación' },
      { heading_pre: 'Confirmaciones y recordatorios, ', heading_highlight: 'resueltos', heading_post: '', copy: 'El seguimiento de confirmaciones en tiempo real os permite saber siempre cómo estáis. Los invitados responden con un toque, las necesidades dietéticas se recogen automáticamente, y quien no haya respondido recibe un aviso discreto desde vuestro panel.', cta: 'Crear tu invitación' },
      { heading_pre: 'Save the Date, ', heading_highlight: 'incluido', heading_post: '', copy: 'Empezad con un Save the Date — es simplemente una opción en la configuración de vuestra invitación. Cuando estéis listos para revelar todos los detalles, cambiad a la invitación completa. Se desbloquean más secciones, el diseño queda exactamente igual, y no hay nada más que pagar.', cta: 'Crear tu invitación' },
      { heading_pre: 'Una apertura con lacre que ', heading_highlight: 'nunca olvidarán', heading_post: '', copy: 'Cada invitación se abre con un sobre animado sellado con lacre — un momento de anticipación antes de que los invitados lean una sola palabra. Elegid entre 18 estilos de sobre, cada uno con su propio sello, y añadidlo con un clic.', cta: 'Crear tu invitación' },
    ],
    features_grid_badge: 'Más funciones',
    features_grid_h2_pre: 'Herramientas potentes para ',
    features_grid_h2_highlight: 'cada detalle',
    features_grid_body: 'Gestión de invitados, seguimiento, recordatorios y más. Todo incluido, sin extras.',
    features_grid_items: [
      { title: 'Invitados de día & de noche', description: 'Una sola invitación lo gestiona todo. Los invitados de noche solo ven lo que les corresponde.' },
      { title: 'Vuestro enlace, a vuestra manera', description: 'Personalizad vuestro enlace y seguid exactamente quién lo ha visitado y quién ha respondido.' },
      { title: 'Preguntas personalizadas', description: 'Preguntad lo que queráis — añadid vuestras propias preguntas al formulario de confirmación.' },
      { title: 'Recordatorios de confirmación', description: 'Avisad a los invitados que no han respondido aún — un toque desde vuestro panel.' },
      { title: 'Compartir por enlace, WhatsApp & QR', description: 'Enviadla como vuestros invitados la verán de verdad. Sin necesidad de app.' },
      { title: 'Save the Date + Invitación', description: 'Dos tipos de invitación incluidos. Enviad primero el Save the Date y luego la invitación completa.' },
      { title: 'Necesidades dietéticas', description: 'Recopilad las necesidades dietéticas como parte de la confirmación.' },
      { title: 'Grupos & etiquetas de invitados', description: 'Organizad a los invitados en grupos y gestionadlos por etiqueta.' },
      { title: 'Seguimiento de visitas', description: 'Comprobad quién ha abierto su invitación y quién todavía no la ha visto.' },
      { title: 'Varios lugares', description: '¿Ceremonia en un lugar y recepción en otro? Sin problema.' },
      { title: 'Invitar colaboradores', description: 'Invitad a vuestro pareja, wedding planner o cualquier otra persona a ayudaros.' },
      { title: 'Invitaciones en vivo', description: 'Actualizad la invitación en cualquier momento. Los invitados ven siempre la última versión.' },
      { title: 'Invitados ilimitados', description: 'Invitad a tantas personas como queráis. Sin límites ni cargos adicionales.' },
      { title: 'Control de acompañantes', description: 'Estableced límites de acompañantes por invitado. Recoged nombres e información dietética.' },
      { title: 'Envío por correo electrónico', description: 'Enviad los enlaces de invitación directamente por e-mail desde vuestro panel. Sin copiar y pegar.' },
      { title: 'Mostrar & ocultar por etiqueta', description: 'Controled qué secciones ve cada grupo. Los invitados de noche se saltan los detalles de la ceremonia.' },
      { title: 'Invitaciones públicas & privadas', description: 'Elegid entre un enlace compartido único o enlaces individuales rastreados para cada invitado.' },
      { title: 'Programa & agenda', description: 'Añadid un programa completo del día para que los invitados sepan exactamente qué esperar y cuándo.' },
      { title: 'Importación & exportación CSV', description: 'Importad la lista de invitados desde una hoja de cálculo o exportad vuestros datos en cualquier momento.' },
      { title: 'Invitaciones en varios idiomas', description: 'Escribid vuestra invitación en tantos idiomas como necesitéis. Los invitados eligen su idioma al abrirla.' },
    ],
    pricing_badge: 'Precios',
    pricing_h2_pre: 'Precio sencillo, ',
    pricing_h2_highlight: 'sin tarifas ocultas',
    pricing_body_wedding: 'Un precio único por invitación. Save the Date, invitados de día y de noche — todo incluido. Diseñad gratis, pagad cuando estéis listos.',
    pricing_body_anniversary: 'Un precio lo cubre todo. RSVP en línea, invitados ilimitados, todos los diseños incluidos. Diseñad gratis, pagad cuando estéis listos.',
    pricing_wedding_features: [
      'Save the Date incluido',
      'Invitados ilimitados',
      'Totalmente personalizable',
      '5 plantillas impresionantes',
      'Seguimiento de confirmaciones & recordatorios',
      'Ver quién ha abierto la invitación',
      'Necesidades dietéticas',
      'Control de acompañantes',
      'Organizar invitados con etiquetas',
      'Invitaciones privadas & públicas',
      'Envío por e-mail o enlace público',
      'Código QR disponible',
    ],
    pricing_anniversary_features: [
      'RSVP en línea',
      'Invitados ilimitados',
      'Totalmente personalizable',
      '5 plantillas impresionantes',
      'Seguimiento de confirmaciones & recordatorios',
      'Ver quién ha abierto la invitación',
      'Necesidades dietéticas',
      'Control de acompañantes',
      'Organizar invitados con etiquetas',
      'Invitaciones privadas & públicas',
      'Envío por e-mail o enlace público',
      'Código QR disponible',
    ],
    pricing_anniversary_not_included: [
      'Save the Date',
      'División de invitados de día y de noche',
      'Varios lugares',
    ],
    pricing_anniversary_wedding_link: 'Ver nuestras invitaciones de boda desde £39',
    pricing_cta: 'Crear tu invitación',
    pricing_planner_heading: 'Organizador',
    pricing_planner_subheading: 'Para wedding planners profesionales',
    pricing_planner_features: [
      'Todo lo del plan estándar incluido',
      'Hasta 10 invitaciones activas a la vez',
      'Crea borradores ilimitados',
      'Importa listas de invitados por CSV',
      'Exporta invitados, elecciones de menú & datos dietéticos a CSV',
      'Invita a parejas a gestionar su propia invitación',
      'Soporte prioritario',
    ],
    pricing_planner_cta: 'Empezar',
    pricing_bespoke_pre: '¿Buscáis algo personalizado?',
    pricing_bespoke_link: 'Contactadnos para un diseño a medida',
    pricing_most_popular: 'Más popular',
    pricing_one_off: 'Pago único',
    pricing_one_time_payment: 'Pago único',
    testimonials_badge: 'Opiniones',
    testimonials_heading_pre: 'Amado por parejas ',
    testimonials_heading_highlight: 'en su gran día',
    our_story_badge: 'Nuestra historia',
    our_story_h2_pre: 'Creado por una pareja, ',
    our_story_h2_highlight: 'para parejas',
    our_story_body: 'Cuando empezamos a planificar nuestra propia boda, nos frustraron las plataformas de invitaciones caras, con tarifas ocultas y herramientas de gestión de invitados poco intuitivas. Queríamos algo bonito, sencillo y honesto — así que lo creamos nosotros mismos. Wedsy es todo lo que desearíamos haber tenido: diseños espectaculares, funciones potentes y un precio justo. Sin sorpresas, sin cargos por invitado, solo las herramientas que necesitáis para celebrar vuestro día a vuestra manera.',
    wedding_planner_h2_pre: '¿Sois ',
    wedding_planner_h2_highlight: 'wedding planners',
    wedding_planner_h2_post: ' o gestionáis un espacio?',
    wedding_planner_body: 'Gestionad hasta 10 invitaciones activas con herramientas de nivel profesional. Un precio sencillo, sin costes ocultos.',
    wedding_planner_features: [
      { title: 'Hasta 10 invitaciones activas', description: 'Suscripción de £29/mes. Gestionad hasta 10 invitaciones activas, invitados ilimitados. Sin cargos por invitado.' },
      { title: 'Operaciones en masa', description: 'Seleccionad varios invitados y realizad acciones con un clic — enviar e-mails, cambiar confirmaciones o reasignar invitaciones.' },
      { title: 'Mostrar & ocultar por etiqueta', description: 'Controled qué secciones ve cada grupo. Los invitados de noche se saltan los detalles de la ceremonia.' },
      { title: 'Necesidades dietéticas', description: 'Recoged las necesidades dietéticas como parte de la confirmación — todo registrado automáticamente en vuestro panel.' },
      { title: 'Importación & exportación CSV', description: 'Importad listas de invitados desde hojas de cálculo o exportad todos vuestros datos en cualquier momento.' },
      { title: 'Personalización completa', description: 'Elegid entre 32 combinaciones de tipografías y decenas de paletas de colores. Cada invitación perfectamente adaptada.' },
    ],
    wedding_planner_cta: 'Empezar',
    seating_planner_badge: 'Plano de asientos',
    seating_planner_h2: 'Planifica cada asiento.\nTodo en un solo lugar.',
    seating_planner_body: 'Diseñad la distribución de vuestra sala en un lienzo interactivo, asignad invitados a sus asientos y exportad un bonito PDF — todo lo que necesita vuestro espacio, listo antes de la boda.',
    seating_planner_bullets: [
      'Mueve las mesas donde quieras — redondas, rectangulares o cuadradas',
      'Asigna invitados a sus asientos con arrastrar y soltar',
      'Indicadores de dieta y alergias visibles de un vistazo',
      'Exporta un PDF listo para imprimir para vuestro espacio',
    ],
    seating_planner_cta_link: 'Descubre el plano de asientos',
    seating_planner_price_label: '· Complemento único',
    templates_badge: 'Diseño exclusivo',
    templates_h2_pre: 'Diseños de los que vuestros invitados ',
    templates_h2_highlight: 'hablarán',
    templates_body: 'Plantillas de invitación animadas y elegantes, creadas para parejas modernas. Cada plantilla está diseñada para sentirse personal y premium.',
    theme_demos_badge: 'Demos de temas',
    theme_demos_heading_pre: 'Míralos ',
    theme_demos_heading_highlight: 'en directo',
    theme_demos_subheading: 'Cada tema es una invitación real — tócala para explorarla como tus invitados.',
    theme_demos_cta: 'Crear tu invitación',
    templates_cta: 'Crear tu invitación',
    footer_cta_h2: '¿Listos para crear vuestra invitación?',
    footer_cta_body: 'Hermosas invitaciones digitales con seguimiento de confirmaciones y gestión de invitados. Todo incluido, un precio sencillo.',
    footer_cta_primary: 'Crear tu invitación',
    footer_cta_login: 'Iniciar sesión',
    footer_brand_blurb: 'Hermosas invitaciones de boda digitales con seguimiento de confirmaciones y gestión de invitados integrados.',
    footer_contact_link: 'Envianos un mensaje',
    footer_col_invitations: 'Invitaciones',
    footer_col_tools: 'Herramientas',
    footer_col_company: 'Empresa',
    footer_copyright: 'Todos los derechos reservados.',
  },

  it: {
    hero_h1_main: 'Il vostro matrimonio.',
    hero_h1_highlight: 'Invitati con stile.',
    hero_subtitle:
      'Inviti digitali straordinari che i vostri ospiti ameranno davvero — con gestione delle conferme, degli invitati e tutto ciò che vi serve per organizzare al meglio il vostro grande giorno.',
    hero_cta_primary: 'Crea il tuo invito',
    hero_cta_secondary: 'Scopri come funziona',
    hero_social_1: 'Amato dalle coppie nel loro grande giorno',
    hero_social_2: 'Ospiti illimitati inclusi',
    hero_social_3: 'Ospiti di giorno e di sera',
    nav_features: 'Funzionalità',
    nav_themes: 'Design',
    nav_how_it_works: 'Come funziona',
    nav_pricing: 'Prezzi',
    nav_faq: 'Domande',
    nav_login: 'Accedi',
    nav_cta: 'Crea invito',
    nav_app: "Vai all'app",
    faq_heading: 'Domande',
    faq_heading_highlight: 'frequenti',
    faq_subtitle: 'Hai domande? Abbiamo le risposte. Non trovi quello che cerchi?',
    faq_contact_link: 'Contattaci',
    faq_contact_suffix: '.',
    emotive_statements: [
      {
        heading: 'La vostra storia d\'amore merita più di una chat di gruppo',
        highlight: 'chat di gruppo',
        copy: "Ogni coppia è unica. Il vostro invito dovrebbe esserlo altrettanto — un primo splendido assaggio del giorno che avete sempre sognato.",
      },
      {
        heading: 'La prima impressione dà il tono',
        highlight: 'dà il tono',
        copy: "Il vostro invito è la primissima cosa che vedranno i vostri ospiti. Fatelo diventare qualcosa di cui si parlerà — qualcosa che accende l'entusiasmo dal momento in cui lo aprono.",
      },
      {
        heading: 'Meno burocrazia, più attesa',
        highlight: 'attesa',
        copy: "Dedicate il vostro tempo a scegliere i fiori, non ad inseguire le conferme. Lasciate che il vostro invito gestisca i dettagli mentre voi vi concentrate su ciò che conta davvero.",
      },
    ],
    faq_items: [
      {
        question: 'I miei ospiti devono scaricare un\'app?',
        answer:
          "Nessuna app richiesta. Gli ospiti aprono il link dell'invito in qualsiasi browser su qualsiasi dispositivo — iPhone, Android o desktop. Possono rispondere all'istante senza scaricare nulla né creare un account.",
      },
      {
        question: 'Posso invitare separatamente gli ospiti del giorno e della sera?',
        answer:
          "Sì. Contrassegna gli ospiti come solo serali e vedranno automaticamente il programma e i dettagli giusti. Gli ospiti del giorno vedono l'intera giornata. Un solo invito gestisce entrambi.",
      },
      {
        question: 'Come rispondono gli ospiti?',
        answer:
          "Gli ospiti toccano il link dell'invito, visualizzano l'invito e rispondono direttamente. Nessuna app da scaricare, nessun account necessario. Vedete la loro risposta all'istante sulla vostra dashboard.",
      },
      {
        question: 'Posso aggiornare il mio invito dopo averlo inviato?',
        answer:
          "Sì. Il vostro invito è live — aggiornate qualsiasi dettaglio in qualsiasi momento e gli ospiti vedranno sempre la versione più recente. Non è necessario reinviare.",
      },
      {
        question: 'Posso vedere un\'anteprima prima di pagare?',
        answer:
          "Sì. Sfogliate i nostri modelli, ammirate le animazioni ed esplorate le funzionalità. Pagate solo quando siete pronti a pubblicare.",
      },
    ],
    how_it_works_badge: 'Come funziona',
    how_it_works_h2_pre: 'Pronto in ',
    how_it_works_h2_highlight: 'minuti',
    how_it_works_h2_post: ', non in mesi',
    how_it_works_body: 'Dalla scelta all\'invio, il vostro invito di nozze è a soli quattro semplici passi.',
    how_it_works_steps: [
      { title: 'Scegli il tuo modello', description: 'Sfoglia la nostra galleria e scegli un design che vi rappresenti.' },
      { title: 'Aggiungi i tuoi dettagli', description: 'Il nostro assistente vi guida attraverso nomi, date, luoghi e altro ancora. Fatto in pochi minuti.' },
      { title: 'Aggiungi i tuoi ospiti', description: 'Importa o aggiungi la tua lista ospiti. Contrassegna gli ospiti serali perché vedano le informazioni giuste.' },
      { title: 'Condividi & monitora', description: 'Invia il link del tuo invito e guarda le conferme arrivare sulla tua dashboard.' },
    ],
    headline_features_badge: 'Funzionalità',
    headline_features_h2_pre: 'Tutto ciò di cui avete bisogno, ',
    headline_features_h2_highlight: 'niente di superfluo',
    headline_features_body: 'Tutte le funzionalità sono incluse. Nessun componente aggiuntivo, nessun livello, nessun costo per ospite.',
    headline_feature_blocks: [
      { heading_pre: 'Rendetelo ', heading_highlight: 'vostro', heading_post: '', copy: 'Ogni dettaglio è nelle vostre mani. Aggiungete nomi, date, luoghi, programma e foto in pochi minuti con il nostro semplice assistente. Mostrate o nascondete sezioni in base al tipo di ospite e aggiornate tutto in qualsiasi momento. Il vostro invito è live, così gli ospiti vedono sempre l\'ultima versione.', cta: 'Scopri come funziona' },
      { heading_pre: 'La vostra lista ospiti, perfettamente ', heading_highlight: 'organizzata', heading_post: '', copy: 'Wedsy è il vostro hub per la gestione degli ospiti. Monitorate le conferme in tempo reale, scoprite chi ha aperto l\'invito, gestite gli ospiti del giorno e della sera, accompagnatori ed esigenze alimentari, e sollecitate chi non ha ancora risposto. Tutto da un\'unica dashboard.', cta: 'Crea il tuo invito' },
      { heading_pre: 'Conferme e promemoria, ', heading_highlight: 'gestiti', heading_post: '', copy: 'Il monitoraggio delle conferme in tempo reale vi permette di sapere sempre a che punto siete. Gli ospiti rispondono con un tocco, le esigenze alimentari vengono raccolte automaticamente e chi non ha risposto riceve un gentile promemoria direttamente dalla vostra dashboard.', cta: 'Crea il tuo invito' },
      { heading_pre: 'Save the Date, ', heading_highlight: 'incluso', heading_post: '', copy: 'Iniziate con un Save the Date — è semplicemente un\'opzione nelle impostazioni del vostro invito. Quando siete pronti a svelare tutti i dettagli, passate all\'invito completo. Si sbloccano più sezioni, il design rimane identico e non c\'è nulla di più da pagare.', cta: 'Crea il tuo invito' },
      { heading_pre: 'Un\'apertura con ceralacca che non ', heading_highlight: 'dimenticheranno', heading_post: '', copy: 'Ogni invito si apre con una busta animata sigillata con ceralacca — un momento di attesa prima che gli ospiti leggano una sola parola. Scegliete tra 18 stili di busta, ognuno con il proprio sigillo, e aggiungetelo con un clic.', cta: 'Crea il tuo invito' },
    ],
    features_grid_badge: 'Altre funzionalità',
    features_grid_h2_pre: 'Strumenti potenti per ',
    features_grid_h2_highlight: 'ogni dettaglio',
    features_grid_body: 'Gestione degli ospiti, monitoraggio, promemoria e altro ancora. Tutto incluso, nessun extra.',
    features_grid_items: [
      { title: 'Ospiti del giorno & della sera', description: 'Un unico invito gestisce entrambi. Gli ospiti serali vedono solo ciò che li riguarda.' },
      { title: 'Il vostro link, a modo vostro', description: 'Personalizzate il vostro link e tracciate esattamente chi lo ha visualizzato e chi ha risposto.' },
      { title: 'Domande personalizzate', description: 'Chiedete agli ospiti tutto ciò che volete — aggiungete le vostre domande al modulo di conferma.' },
      { title: 'Promemoria RSVP', description: 'Sollecitate gli ospiti che non hanno ancora risposto — un tocco dalla vostra dashboard.' },
      { title: 'Condividi via link, WhatsApp & QR', description: 'Inviatelo come i vostri ospiti lo vedranno davvero. Nessuna app necessaria.' },
      { title: 'Save the Date + Invito', description: 'Due tipi di invito inclusi. Inviate prima il Save the Date, poi l\'invito completo.' },
      { title: 'Esigenze alimentari', description: 'Raccogliete le esigenze alimentari come parte della conferma.' },
      { title: 'Gruppi & tag ospiti', description: 'Organizzate gli ospiti in gruppi e gestitelti per tag.' },
      { title: 'Monitoraggio delle visualizzazioni', description: 'Scoprite chi ha aperto il proprio invito e chi non lo ha ancora visto.' },
      { title: 'Più sedi', description: 'Cerimonia in un posto, ricevimento in un altro? Nessun problema.' },
      { title: 'Invitare collaboratori', description: 'Invitate il vostro partner, il wedding planner o chiunque altro ad aiutarvi.' },
      { title: 'Inviti live', description: 'Aggiornate il vostro invito in qualsiasi momento. Gli ospiti vedono sempre l\'ultima versione.' },
      { title: 'Ospiti illimitati', description: 'Invitate quante persone volete. Nessun limite, nessun costo aggiuntivo.' },
      { title: 'Controllo degli accompagnatori', description: 'Impostate limiti di accompagnatori per ospite. Raccogliete nomi e informazioni alimentari.' },
      { title: 'Invio via e-mail', description: 'Inviate i link dell\'invito direttamente via e-mail dalla vostra dashboard. Senza copia e incolla.' },
      { title: 'Mostra & nascondi per tag', description: 'Controllate quali sezioni vede ogni gruppo. Gli ospiti serali saltano i dettagli della cerimonia.' },
      { title: 'Inviti pubblici & privati', description: 'Scegliete tra un unico link condivisibile o link individuali tracciati per ogni ospite.' },
      { title: 'Programma & timeline', description: 'Aggiungete un programma completo della giornata per far sapere agli ospiti cosa aspettarsi e quando.' },
      { title: 'Importazione & esportazione CSV', description: 'Importate la vostra lista ospiti da un foglio di calcolo o esportate i dati in qualsiasi momento.' },
      { title: 'Inviti multilingue', description: 'Scrivete il vostro invito in quante lingue volete. Gli ospiti scelgono la loro lingua all\'apertura.' },
    ],
    pricing_badge: 'Prezzi',
    pricing_h2_pre: 'Prezzi semplici, ',
    pricing_h2_highlight: 'nessuna tariffa nascosta',
    pricing_body_wedding: 'Un unico prezzo per invito. Save the Date, ospiti del giorno e della sera — tutto incluso. Progettate gratis, pagate quando siete pronti.',
    pricing_body_anniversary: 'Un unico prezzo che copre tutto. RSVP online, ospiti illimitati, tutti i design inclusi. Progettate gratis, pagate quando siete pronti.',
    pricing_wedding_features: [
      'Save the Date incluso',
      'Ospiti illimitati',
      'Completamente personalizzabile',
      '5 modelli straordinari',
      'Monitoraggio RSVP & promemoria',
      "Scopri chi ha aperto l'invito",
      'Esigenze alimentari',
      'Controllo degli accompagnatori',
      'Organizza gli ospiti con i tag',
      'Inviti privati & pubblici',
      'Invio via e-mail o link pubblico',
      'Codice QR disponibile',
    ],
    pricing_anniversary_features: [
      'RSVP online',
      'Ospiti illimitati',
      'Completamente personalizzabile',
      '5 modelli straordinari',
      'Monitoraggio RSVP & promemoria',
      "Scopri chi ha aperto l'invito",
      'Esigenze alimentari',
      'Controllo degli accompagnatori',
      'Organizza gli ospiti con i tag',
      'Inviti privati & pubblici',
      'Invio via e-mail o link pubblico',
      'Codice QR disponibile',
    ],
    pricing_anniversary_not_included: [
      'Save the Date',
      'Divisione ospiti giorno & sera',
      'Più sedi',
    ],
    pricing_anniversary_wedding_link: 'Scopri i nostri inviti di nozze da £39',
    pricing_cta: 'Crea il tuo invito',
    pricing_planner_heading: 'Planner',
    pricing_planner_subheading: 'Per wedding planner professionisti',
    pricing_planner_features: [
      'Tutto incluso nel piano standard',
      'Fino a 10 inviti attivi contemporaneamente',
      'Crea bozze illimitate',
      'Importa liste ospiti via CSV',
      'Esporta ospiti, scelte pasto & info alimentari in CSV',
      'Invita le coppie a gestire il proprio invito',
      'Supporto prioritario',
    ],
    pricing_planner_cta: 'Inizia ora',
    pricing_bespoke_pre: 'Cercate qualcosa di personalizzato?',
    pricing_bespoke_link: 'Contattateci per un design su misura',
    pricing_most_popular: 'Più popolare',
    pricing_one_off: 'Acquisto unico',
    pricing_one_time_payment: 'Pagamento unico',
    testimonials_badge: 'Recensioni',
    testimonials_heading_pre: 'Amato dalle coppie ',
    testimonials_heading_highlight: 'nel loro grande giorno',
    our_story_badge: 'La nostra storia',
    our_story_h2_pre: 'Creato da una coppia, ',
    our_story_h2_highlight: 'per le coppie',
    our_story_body: 'Quando abbiamo iniziato a pianificare il nostro matrimonio, eravamo frustrati dalle piattaforme di inviti costose, con tariffe nascoste e strumenti di gestione degli ospiti poco pratici. Volevamo qualcosa di bello, semplice e onesto — così lo abbiamo creato noi stessi. Wedsy è tutto ciò che avremmo voluto avere: design straordinari, funzionalità potenti e un prezzo equo. Nessuna sorpresa, nessun costo per ospite, solo gli strumenti di cui avete bisogno per celebrare il vostro giorno a modo vostro.',
    wedding_planner_h2_pre: 'Siete ',
    wedding_planner_h2_highlight: 'wedding planner',
    wedding_planner_h2_post: ' o gestite una location?',
    wedding_planner_body: 'Gestite fino a 10 inviti attivi con strumenti di livello professionale. Un prezzo semplice, senza costi nascosti.',
    wedding_planner_features: [
      { title: 'Fino a 10 inviti attivi', description: 'Abbonamento £29/mese. Gestite fino a 10 inviti attivi, ospiti illimitati. Nessun costo per ospite.' },
      { title: 'Operazioni in blocco', description: 'Selezionate più ospiti ed eseguite azioni con un clic — inviare e-mail, modificare le conferme o riassegnare gli inviti.' },
      { title: 'Mostra & nascondi per tag', description: 'Controllate quali sezioni vede ogni gruppo. Gli ospiti serali saltano i dettagli della cerimonia.' },
      { title: 'Esigenze alimentari', description: 'Raccogliete le esigenze alimentari come parte della conferma — tutto tracciato automaticamente nella vostra dashboard.' },
      { title: 'Importazione & esportazione CSV', description: 'Importate liste ospiti da fogli di calcolo o esportate tutti i dati in qualsiasi momento.' },
      { title: 'Personalizzazione completa', description: 'Scegliete tra 32 abbinamenti tipografici e decine di palette colori. Ogni invito perfettamente in linea con il brand.' },
    ],
    wedding_planner_cta: 'Inizia ora',
    seating_planner_badge: 'Piano dei posti',
    seating_planner_h2: 'Pianifica ogni posto.\nTutto in un unico posto.',
    seating_planner_body: 'Progettate il layout della sala su una canvas interattiva, trascinate gli ospiti sui posti e esportate un bellissimo PDF — tutto ciò di cui la vostra location ha bisogno, pronto prima del matrimonio.',
    seating_planner_bullets: [
      'Sposta i tavoli ovunque — rotondi, rettangolari o quadrati',
      'Assegna gli ospiti ai posti con il trascinamento',
      'Indicatori di dieta e allergie visibili a colpo d\'occhio',
      'Esporta un PDF pronto per la stampa per la vostra location',
    ],
    seating_planner_cta_link: 'Scopri il piano dei posti',
    seating_planner_price_label: '· Componente aggiuntivo una tantum',
    templates_badge: 'Design su misura',
    templates_h2_pre: 'Design di cui i vostri ospiti ',
    templates_h2_highlight: 'parleranno',
    templates_body: 'Modelli di invito animati ed eleganti, pensati per le coppie moderne. Ogni modello è progettato per sembrare personale e di alta qualità.',
    theme_demos_badge: 'Demo dei temi',
    theme_demos_heading_pre: 'Guardateli ',
    theme_demos_heading_highlight: 'dal vivo',
    theme_demos_subheading: 'Ogni tema è un invito reale — toccatelo per esplorarlo come i vostri ospiti.',
    theme_demos_cta: 'Crea il tuo invito',
    templates_cta: 'Crea il tuo invito',
    footer_cta_h2: 'Pronti a creare il vostro invito?',
    footer_cta_body: 'Bellissimi inviti digitali con monitoraggio RSVP e gestione degli ospiti. Tutto incluso, un prezzo semplice.',
    footer_cta_primary: 'Crea il tuo invito',
    footer_cta_login: 'Accedi',
    footer_brand_blurb: 'Bellissimi inviti di nozze digitali con monitoraggio RSVP e gestione degli ospiti integrati.',
    footer_contact_link: 'Inviateci un messaggio',
    footer_col_invitations: 'Inviti',
    footer_col_tools: 'Strumenti',
    footer_col_company: 'Azienda',
    footer_copyright: 'Tutti i diritti riservati.',
  },

  pt: {
    hero_h1_main: 'O vosso casamento.',
    hero_h1_highlight: 'Lindamente convidado.',
    hero_subtitle:
      'Convites digitais deslumbrantes que os vossos convidados vão adorar — com confirmação de presença integrada, gestão de convidados e tudo o que precisam para o vosso grande dia correr na perfeição.',
    hero_cta_primary: 'Criar o Vosso Convite',
    hero_cta_secondary: 'Ver como funciona',
    hero_social_1: 'Adorado por casais no seu grande dia',
    hero_social_2: 'Convidados ilimitados incluídos',
    hero_social_3: 'Convidados de dia e de noite',
    nav_features: 'Funcionalidades',
    nav_themes: 'Temas',
    nav_how_it_works: 'Como funciona',
    nav_pricing: 'Preços',
    nav_faq: 'FAQ',
    nav_login: 'Iniciar sessão',
    nav_cta: 'Criar Convite',
    nav_app: 'Ir para a App',
    faq_heading: 'Perguntas',
    faq_heading_highlight: 'frequentes',
    faq_subtitle: 'Têm perguntas? Nós temos respostas. Não encontram o que procuram?',
    faq_contact_link: 'Contactem-nos',
    faq_contact_suffix: '.',
    emotive_statements: [
      {
        heading: 'A vossa história de amor merece mais do que um grupo de mensagens',
        highlight: 'grupo de mensagens',
        copy: "Cada casal é único. O vosso convite deve ser igualmente especial — um primeiro vislumbre deslumbrante do dia que sempre sonharam.",
      },
      {
        heading: 'A primeira impressão define o tom',
        highlight: 'define o tom',
        copy: "O vosso convite é a primeira coisa que os vossos convidados vão ver. Façam dele algo de que vão falar — algo que desperta a emoção desde o momento em que o abrem.",
      },
      {
        heading: 'Menos burocracia, mais antecipação',
        highlight: 'antecipação',
        copy: "Dediquem o vosso tempo a escolher as flores, não a perseguir confirmações. Deixem que o vosso convite trate dos detalhes enquanto se concentram no que realmente importa.",
      },
    ],
    faq_items: [
      {
        question: 'Os meus convidados precisam de descarregar uma app?',
        answer:
          'Não é necessária nenhuma app. Os convidados abrem o link do convite em qualquer browser, em qualquer dispositivo — iPhone, Android ou computador. Podem confirmar a presença de imediato, sem descarregar nada nem criar uma conta.',
      },
      {
        question: 'Posso convidar separadamente os convidados do dia e da noite?',
        answer:
          'Sim. Identifiquem os convidados como apenas de noite e eles verão automaticamente o programa e os detalhes certos. Os convidados do dia veem o dia completo. Um único convite trata de ambos.',
      },
      {
        question: 'Como confirmam os convidados?',
        answer:
          'Os convidados tocam no link do convite, visualizam o convite e respondem diretamente. Sem app para descarregar, sem conta necessária. Veem a resposta deles de imediato no vosso painel.',
      },
      {
        question: 'Posso atualizar o meu convite depois de o enviar?',
        answer:
          'Sim. O vosso convite está online — atualizem qualquer detalhe a qualquer momento e os convidados verão sempre a versão mais recente. Não é necessário reenviar.',
      },
      {
        question: 'Posso ver uma pré-visualização antes de pagar?',
        answer:
          'Sim. Explorem os nossos modelos, vejam as animações e descubram as funcionalidades. Só pagam quando estiverem prontos para publicar.',
      },
    ],
    how_it_works_badge: 'Como funciona',
    how_it_works_h2_pre: 'Pronto em ',
    how_it_works_h2_highlight: 'minutos',
    how_it_works_h2_post: ', não em meses',
    how_it_works_body: 'Desde a escolha até ao envio, o vosso convite de casamento está a apenas quatro passos simples.',
    how_it_works_steps: [
      { title: 'Escolhe o teu modelo', description: 'Explora a nossa galeria e escolhe um design que vos represente.' },
      { title: 'Adiciona os vossos dados', description: 'O nosso assistente guia-vos pelos nomes, datas, locais e muito mais. Pronto em minutos.' },
      { title: 'Adiciona os vossos convidados', description: 'Importa ou adiciona a vossa lista de convidados. Marca os convidados de noite para que vejam a informação certa.' },
      { title: 'Partilha & acompanha', description: 'Envia o link do convite e observa as confirmações chegarem ao vosso painel.' },
    ],
    headline_features_badge: 'Funcionalidades',
    headline_features_h2_pre: 'Tudo o que precisam, ',
    headline_features_h2_highlight: 'nada que sobre',
    headline_features_body: 'Todas as funcionalidades estão incluídas. Sem extras, sem níveis, sem taxas por convidado.',
    headline_feature_blocks: [
      { heading_pre: 'Façam-no ', heading_highlight: 'vosso', heading_post: '', copy: 'Cada detalhe está nas vossas mãos. Adicionem nomes, datas, locais, programa e fotos em minutos com o nosso assistente simples. Mostrem ou ocultem secções consoante o tipo de convidado e atualizem tudo em qualquer altura. O vosso convite está online, para que os convidados vejam sempre a versão mais recente.', cta: 'Ver como funciona' },
      { heading_pre: 'A vossa lista de convidados, perfeitamente ', heading_highlight: 'organizada', heading_post: '', copy: 'Wedsy é o vosso centro de gestão de convidados. Acompanhem as confirmações em tempo real, vejam quem abriu o convite, gerenciem convidados de dia e de noite, acompanhantes e necessidades alimentares, e lembrem quem ainda não respondeu. Tudo num único painel.', cta: 'Criar o Vosso Convite' },
      { heading_pre: 'Confirmações e lembretes, ', heading_highlight: 'tratados', heading_post: '', copy: 'O acompanhamento de confirmações em tempo real permite-vos saber sempre como estão as coisas. Os convidados respondem com um toque, as necessidades alimentares são recolhidas automaticamente, e quem não respondeu recebe um lembrete discreto a partir do vosso painel.', cta: 'Criar o Vosso Convite' },
      { heading_pre: 'Save the Date, ', heading_highlight: 'incluído', heading_post: '', copy: 'Comecem com um Save the Date — é simplesmente uma opção nas definições do vosso convite. Quando estiverem prontos para revelar todos os detalhes, mudem para o convite completo. Desbloqueiam-se mais secções, o design fica exatamente igual e não há mais nada a pagar.', cta: 'Criar o Vosso Convite' },
      { heading_pre: 'Uma abertura com lacre que ', heading_highlight: 'nunca esquecerão', heading_post: '', copy: 'Cada convite abre-se com um envelope animado selado com lacre — um momento de antecipação antes de os convidados lerem uma única palavra. Escolham entre 18 estilos de envelope, cada um com o seu próprio selo, e adicionem-no com um clique.', cta: 'Criar o Vosso Convite' },
    ],
    features_grid_badge: 'Mais funcionalidades',
    features_grid_h2_pre: 'Ferramentas poderosas para ',
    features_grid_h2_highlight: 'cada detalhe',
    features_grid_body: 'Gestão de convidados, acompanhamento, lembretes e muito mais. Tudo incluído, sem extras.',
    features_grid_items: [
      { title: 'Convidados de dia & de noite', description: 'Um único convite trata de ambos. Os convidados de noite só veem o que lhes diz respeito.' },
      { title: 'O vosso link, à vossa maneira', description: 'Personalizem o vosso link e acompanhem exatamente quem o visitou e quem respondeu.' },
      { title: 'Perguntas personalizadas', description: 'Perguntem aos convidados o que quiserem — adicionem as vossas próprias perguntas ao formulário de confirmação.' },
      { title: 'Lembretes de confirmação', description: 'Avisem os convidados que ainda não responderam — um toque no vosso painel.' },
      { title: 'Partilhar por link, WhatsApp & QR', description: 'Enviem como os convidados o verão de verdade. Sem necessidade de app.' },
      { title: 'Save the Date + Convite', description: 'Dois tipos de convite incluídos. Enviem primeiro o Save the Date e depois o convite completo.' },
      { title: 'Necessidades alimentares', description: 'Recolham as necessidades alimentares como parte da confirmação.' },
      { title: 'Grupos & etiquetas de convidados', description: 'Organizem os convidados em grupos e gerenciem-nos por etiqueta.' },
      { title: 'Acompanhamento de visualizações', description: 'Vejam quem abriu o seu convite e quem ainda não o viu.' },
      { title: 'Vários locais', description: 'Cerimónia num sítio, receção noutro? Sem problema.' },
      { title: 'Convidar colaboradores', description: 'Convidem o vosso parceiro, wedding planner ou qualquer outra pessoa para ajudar.' },
      { title: 'Convites em tempo real', description: 'Atualizem o convite a qualquer momento. Os convidados veem sempre a versão mais recente.' },
      { title: 'Convidados ilimitados', description: 'Convidem quantas pessoas quiserem. Sem limites, sem custos adicionais.' },
      { title: 'Controlo de acompanhantes', description: 'Definam limites de acompanhantes por convidado. Recolham nomes e informações alimentares.' },
      { title: 'Envio por e-mail', description: 'Enviem links de convite diretamente por e-mail a partir do vosso painel. Sem copiar e colar.' },
      { title: 'Mostrar & ocultar por etiqueta', description: 'Controlem quais as secções que cada grupo vê. Os convidados de noite não veem os detalhes da cerimónia.' },
      { title: 'Convites públicos & privados', description: 'Escolham entre um único link partilhável ou links individuais rastreados para cada convidado.' },
      { title: 'Programa & agenda', description: 'Adicionem um programa completo do dia para que os convidados saibam exatamente o que esperar e quando.' },
      { title: 'Importação & exportação CSV', description: 'Importem a vossa lista de convidados de uma folha de cálculo ou exportem os vossos dados a qualquer momento.' },
      { title: 'Convites em vários idiomas', description: 'Escrevam o vosso convite em tantos idiomas quantos precisarem. Os convidados escolhem o seu idioma ao abrir.' },
    ],
    pricing_badge: 'Preços',
    pricing_h2_pre: 'Preço simples, ',
    pricing_h2_highlight: 'sem taxas ocultas',
    pricing_body_wedding: 'Um preço único por convite. Save the Date, convidados de dia e de noite — tudo incluído. Desenhem grátis, paguem quando estiverem prontos.',
    pricing_body_anniversary: 'Um preço cobre tudo. RSVP online, convidados ilimitados, todos os designs incluídos. Desenhem grátis, paguem quando estiverem prontos.',
    pricing_wedding_features: [
      'Save the Date incluído',
      'Convidados ilimitados',
      'Totalmente personalizável',
      '5 modelos deslumbrantes',
      'Acompanhamento de confirmações & lembretes',
      'Ver quem abriu o convite',
      'Necessidades alimentares',
      'Controlo de acompanhantes',
      'Organizar convidados com etiquetas',
      'Convites privados & públicos',
      'Envio por e-mail ou link público',
      'Código QR disponível',
    ],
    pricing_anniversary_features: [
      'RSVP online',
      'Convidados ilimitados',
      'Totalmente personalizável',
      '5 modelos deslumbrantes',
      'Acompanhamento de confirmações & lembretes',
      'Ver quem abriu o convite',
      'Necessidades alimentares',
      'Controlo de acompanhantes',
      'Organizar convidados com etiquetas',
      'Convites privados & públicos',
      'Envio por e-mail ou link público',
      'Código QR disponível',
    ],
    pricing_anniversary_not_included: [
      'Save the Date',
      'Divisão de convidados de dia & noite',
      'Vários locais',
    ],
    pricing_anniversary_wedding_link: 'Ver os nossos convites de casamento a partir de £39',
    pricing_cta: 'Criar o Vosso Convite',
    pricing_planner_heading: 'Organizador',
    pricing_planner_subheading: 'Para wedding planners profissionais',
    pricing_planner_features: [
      'Tudo incluído no plano standard',
      'Até 10 convites ativos em simultâneo',
      'Crie rascunhos ilimitados',
      'Importe listas de convidados por CSV',
      'Exporte convidados, escolhas de refeição & dados alimentares para CSV',
      'Convide casais para gerir o seu próprio convite',
      'Suporte prioritário',
    ],
    pricing_planner_cta: 'Começar',
    pricing_bespoke_pre: 'Procuram algo personalizado?',
    pricing_bespoke_link: 'Contactem-nos para um design à medida',
    pricing_most_popular: 'Mais popular',
    pricing_one_off: 'Pagamento único',
    pricing_one_time_payment: 'Pagamento único',
    testimonials_badge: 'Avaliações',
    testimonials_heading_pre: 'Adorado por casais ',
    testimonials_heading_highlight: 'no seu grande dia',
    our_story_badge: 'A nossa história',
    our_story_h2_pre: 'Criado por um casal, ',
    our_story_h2_highlight: 'para casais',
    our_story_body: 'Quando começámos a planear o nosso próprio casamento, ficámos frustrados com plataformas de convites caras, com taxas ocultas e ferramentas de gestão de convidados pouco práticas. Queríamos algo bonito, simples e honesto — por isso criámo-lo nós próprios. Wedsy é tudo o que desejaríamos ter tido: designs deslumbrantes, funcionalidades poderosas e um preço justo. Sem surpresas, sem taxas por convidado, apenas as ferramentas que precisam para celebrar o vosso dia à vossa maneira.',
    wedding_planner_h2_pre: 'São ',
    wedding_planner_h2_highlight: 'wedding planners',
    wedding_planner_h2_post: ' ou gerem um espaço?',
    wedding_planner_body: 'Gerenciem até 10 convites ativos com ferramentas de nível profissional. Um preço simples, sem custos ocultos.',
    wedding_planner_features: [
      { title: 'Até 10 convites ativos', description: 'Subscrição de £29/mês. Gerenciem até 10 convites ativos, convidados ilimitados. Sem taxas por convidado.' },
      { title: 'Operações em massa', description: 'Selecionem vários convidados e realizem ações com um clique — enviar e-mails, alterar confirmações ou reatribuir convites.' },
      { title: 'Mostrar & ocultar por etiqueta', description: 'Controlem quais as secções que cada grupo vê. Os convidados de noite não veem os detalhes da cerimónia.' },
      { title: 'Necessidades alimentares', description: 'Recolham as necessidades alimentares como parte da confirmação — tudo registado automaticamente no vosso painel.' },
      { title: 'Importação & exportação CSV', description: 'Importem listas de convidados de folhas de cálculo ou exportem todos os dados a qualquer momento.' },
      { title: 'Personalização completa', description: 'Escolham entre 32 combinações tipográficas e dezenas de paletas de cores. Cada convite perfeitamente alinhado com a vossa marca.' },
    ],
    wedding_planner_cta: 'Começar',
    seating_planner_badge: 'Plano de mesa',
    seating_planner_h2: 'Planeiem cada lugar.\nTudo num só sítio.',
    seating_planner_body: 'Desenhem o layout da sala numa canvas interativa, arrastem os convidados para os seus lugares e exportem um bonito PDF — tudo o que o vosso espaço precisa, pronto antes do casamento.',
    seating_planner_bullets: [
      'Movam as mesas para onde quiserem — redondas, retangulares ou quadradas',
      'Atribuam convidados a lugares com arrastar e soltar',
      'Indicadores de dieta e alergias visíveis de relance',
      'Exportem um PDF pronto para imprimir para o vosso espaço',
    ],
    seating_planner_cta_link: 'Saber mais sobre o plano de mesa',
    seating_planner_price_label: '· Complemento único',
    templates_badge: 'Design exclusivo',
    templates_h2_pre: 'Designs de que os vossos convidados ',
    templates_h2_highlight: 'vão falar',
    templates_body: 'Modelos de convite animados e elegantes, criados para casais modernos. Cada modelo é desenhado para ser pessoal e premium.',
    theme_demos_badge: 'Demos de temas',
    theme_demos_heading_pre: 'Veja-os ',
    theme_demos_heading_highlight: 'ao vivo',
    theme_demos_subheading: 'Cada tema é um convite real — toque para explorá-lo como os vossos convidados.',
    theme_demos_cta: 'Criar o Vosso Convite',
    templates_cta: 'Criar o Vosso Convite',
    footer_cta_h2: 'Prontos para criar o vosso convite?',
    footer_cta_body: 'Belos convites digitais com acompanhamento de confirmações e gestão de convidados. Tudo incluído, um preço simples.',
    footer_cta_primary: 'Criar o Vosso Convite',
    footer_cta_login: 'Iniciar sessão',
    footer_brand_blurb: 'Belos convites de casamento digitais com acompanhamento de confirmações e gestão de convidados integrados.',
    footer_contact_link: 'Enviem-nos uma mensagem',
    footer_col_invitations: 'Convites',
    footer_col_tools: 'Ferramentas',
    footer_col_company: 'Empresa',
    footer_copyright: 'Todos os direitos reservados.',
  },
};

export function getHomepageStrings(locale: string): HomepageStrings {
  return LOCALE_STRINGS[locale] ?? LOCALE_STRINGS['en'];
}

export const SUPPORTED_HOMEPAGE_LOCALES = ['fr', 'de', 'es', 'it', 'pt'] as const;

export const LOCALE_FLAGS: Record<string, string> = {
  en: '🇬🇧',
  fr: '🇫🇷',
  de: '🇩🇪',
  es: '🇪🇸',
  it: '🇮🇹',
  pt: '🇵🇹',
};

export const LOCALE_LABELS: Record<string, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  it: 'Italiano',
  pt: 'Português',
};
